/*!
 * TinyPoker 雙週獎池分析 Dashboard
 * 純閱覽頁面：KPI 卡片、趨勢圖表、可排序俱樂部明細表、俱樂部歷史 Modal
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card, CardHeader, CardBody, CardTitle,
  Row, Col, Button, Table, Badge, Alert,
  Input, Modal, ModalHeader, ModalBody,
} from "reactstrap";
import axios from "axios";
import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import { COLORS } from "constant/adminTheme";
import TinyCollapsibleSection from "components/tiny/TinyCollapsibleSection";
import TinyWeekSelector from "../../components/tiny/TinyWeekSelector";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, BarElement, ArcElement,
  Title, Tooltip, Legend, Filler
} from "chart.js";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend, Filler
);

const API_BASE = "/api/tiny";

// ==================== Design System ====================

const S = {
  cardBg: '#1e2530',
  headerBg: '#252d3a',
  inputBg: '#1a1f2b',
  border: 'rgba(255,255,255,0.1)',
  borderLight: 'rgba(255,255,255,0.05)',
  borderInput: 'rgba(255,255,255,0.2)',
  textPrimary: '#fff',
  textSecondary: 'rgba(255,255,255,0.8)',
  textMuted: 'rgba(255,255,255,0.5)',
  accent: '#667eea',
  accentGrad: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  dangerGrad: 'linear-gradient(135deg, #f5365c 0%, #f56036 100%)',
  successGrad: 'linear-gradient(135deg, #2dce89 0%, #26a770 100%)',
  infoGrad: 'linear-gradient(135deg, #11cdef 0%, #1171ef 100%)',
  blueGrad: 'linear-gradient(135deg, #1f8ef1 0%, #0d6efd 100%)',
  tealGrad: 'linear-gradient(135deg, #005580 0%, #007399 100%)',
  botGrad: 'linear-gradient(135deg, #fb6340, #fbb140)',
  hqGrad: 'linear-gradient(135deg, #1f8ef1, #0d6efd)',
};

const thStyle = {
  background: S.headerBg, color: S.textPrimary, fontWeight: 600,
  borderColor: S.border, fontSize: '12px', whiteSpace: 'nowrap',
  padding: '10px 12px',
};
const tdStyle = {
  borderColor: S.borderLight, color: S.textSecondary,
  verticalAlign: 'middle', padding: '8px 12px', fontSize: '13px',
  whiteSpace: 'nowrap',
};
const selectStyle = {
  background: S.inputBg, color: S.textPrimary,
  border: `1px solid ${S.borderInput}`, borderRadius: '6px',
};

const CHART_COLORS = {
  rebate: "#1f8ef1",
  activity: "#00d6b4",
  volume: "#f17e5d",
  total: "#e14eca",
  ecobot: "#fd5d93",
  real: "#1f8ef1",
  hqReturn: "#1f8ef1",
  ecoReturn: "#00d6b4",
  botCost: "#fd5d93",
};

const KPI_GRADIENTS = [
  { grad: 'linear-gradient(135deg, #3a1c71 0%, #5B3A8C 50%, #6441A5 100%)', shadow: '0 4px 12px rgba(100,65,165,0.35)' },
  { grad: 'linear-gradient(135deg, #0c3483 0%, #1565C0 50%, #1976D2 100%)', shadow: '0 4px 12px rgba(21,101,192,0.35)' },
  { grad: 'linear-gradient(135deg, #134e5e 0%, #1B7A4A 50%, #2E7D32 100%)', shadow: '0 4px 12px rgba(46,125,50,0.35)' },
  { grad: 'linear-gradient(135deg, #5c3d12 0%, #BF6D0A 50%, #E67E22 100%)', shadow: '0 4px 12px rgba(230,126,34,0.35)' },
  { grad: 'linear-gradient(135deg, #0d4d5c 0%, #00838F 50%, #0097A7 100%)', shadow: '0 4px 12px rgba(0,151,167,0.35)' },
  { grad: 'linear-gradient(135deg, #6a1b30 0%, #AD1457 50%, #C62828 100%)', shadow: '0 4px 12px rgba(198,40,40,0.35)' },
  { grad: `${S.cardBg}`, shadow: 'none', border: `1px solid ${S.border}` },
];

const FILTER_STYLES = {
  all:       { bg: 'rgba(102,126,234,0.15)', border: 'rgba(102,126,234,0.4)', color: '#667eea', activeBg: S.accentGrad },
  bot:       { bg: 'rgba(251,99,64,0.15)',   border: 'rgba(251,99,64,0.4)',   color: '#fb6340', activeBg: S.botGrad },
  hq:        { bg: 'rgba(17,205,239,0.15)',  border: 'rgba(17,205,239,0.4)',  color: '#11cdef', activeBg: S.infoGrad },
  external:  { bg: 'rgba(149,165,166,0.15)', border: 'rgba(149,165,166,0.4)', color: '#95a5a6', activeBg: 'linear-gradient(135deg, #95a5a6, #7f8c8d)' },
  qualified: { bg: 'rgba(45,206,137,0.15)',  border: 'rgba(45,206,137,0.4)',  color: '#2dce89', activeBg: S.successGrad },
  rebate:    { bg: 'rgba(155,81,224,0.15)',  border: 'rgba(155,81,224,0.4)',  color: '#9b51e0', activeBg: 'linear-gradient(135deg, #9b51e0, #7b2cbf)' },
  pure_share: { bg: 'rgba(232,163,61,0.15)', border: 'rgba(232,163,61,0.4)', color: '#e8a33d', activeBg: 'linear-gradient(135deg, #e8a33d, #c77f1a)' },
};

// v2.17.0 獎池回收模式（pool_mode_snapshot）標籤
const POOL_MODE_LABELS = {
  recover_only: "純分潤（全回收）",
  recover_activity: "活躍回收",
  recover_loss_refund: "輸退回收",
};

// ==================== Formatters ====================

const fmt = (v) => {
  if (v === null || v === undefined) return "0.00";
  return Number(v).toLocaleString("zh-TW", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtInt = (v) => {
  if (v === null || v === undefined) return "0";
  return Math.round(v).toLocaleString("zh-TW");
};
const fmtK = (v) => {
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + "M";
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(0) + "K";
  return v.toFixed(0);
};
const fmtPct = (v) => (v === null || v === undefined ? "0%" : v.toFixed(1) + "%");

/**
 * 輸退獎池佔「水前輸贏(扣JP)」的比例 —— 用來一眼看出這家的 Rebate 大概給到幾 %。
 * 分母取絕對值（水前輸贏是負數＝輸錢，才會有輸退），贏錢或沒配到輸退獎池就不顯示。
 */
const rebatePct = (pool, preRakePl) => {
  const v = Number(pool) || 0;
  const base = Number(preRakePl) || 0;
  if (v <= 0 || base >= 0) return null;
  return (v / Math.abs(base)) * 100;
};

/**
 * 整期的輸退趴數。輸退獎池是按「輸多少」等比分配，所以每家達標俱樂部的趴數必定相同，
 * 用總額 ÷ 總輸額算一個就代表全部（逐列重複顯示沒意義）。
 * 分母只算「真的有分到輸退」的俱樂部 —— 未達標的那些 rebate=0 但也在輸錢，
 * 算進去會把趴數拉低變成假的數字。
 */
const periodRebatePct = (rows, getPool, getPre) => {
  let pool = 0, loss = 0;
  for (const r of rows || []) {
    const v = Number(getPool(r)) || 0;
    const base = Number(getPre(r)) || 0;
    if (v > 0 && base < 0) { pool += v; loss += Math.abs(base); }
  }
  return loss > 0 ? (pool / loss) * 100 : null;
};

const RebatePctTag = ({ pool, preRakePl, size = 11 }) => {
  const p = rebatePct(pool, preRakePl);
  if (p === null) return null;
  return (
    <span style={{ color: "rgba(255,255,255,0.45)", fontSize: size, marginLeft: 4 }}>
      ({p.toFixed(1)}%)
    </span>
  );
};

/**
 * 把技術性調整 Modal 的表單轉成後端 override body。
 * 試算(simulate)與送出(commit)共用同一條 —— v2.28.3 就是因為兩邊各寫一份、
 * 試算那份漏帶 water_deduction，導致「改扣水量畫面不會重算」但送出時卻真的生效。
 */
const buildOverrides = (form) => {
  const num = (obj) => Object.fromEntries(
    Object.entries(obj || {}).filter(([, v]) => v !== "" && v != null && !isNaN(Number(v)))
      .map(([k, v]) => [k, Number(v)]));
  const water = num(form.water);
  // 有填任一格扣水量就走新式；兩格都空才退回舊式的 pool override（相容既有調整紀錄）
  const useWater = water.ecobot != null || water.self_bot != null;
  return {
    pre_rake_pl: num(form.pre),
    water_deduction: useWater ? water : {},
    pool: useWater ? {} : num(form.pool),
  };
};

// ==================== Chart Config ====================

const chartTooltipCb = {
  label: (ctx) => {
    let l = ctx.dataset.label || "";
    if (l) l += ": ";
    return l + fmtInt(ctx.parsed.y);
  },
};

const baseChartOpts = (title) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true, position: "top",
      labels: { color: "#fff", font: { size: 12 }, padding: 15 },
    },
    tooltip: {
      backgroundColor: "rgba(0,0,0,0.85)", padding: 12,
      titleFont: { size: 13 }, bodyFont: { size: 12 },
      callbacks: chartTooltipCb,
    },
    title: title ? { display: true, text: title, color: "#fff", font: { size: 14 } } : undefined,
  },
  scales: {
    x: {
      ticks: { color: "#9A9A9A", font: { size: 10 }, maxRotation: 45 },
      grid: { color: "rgba(255,255,255,0.05)", display: false },
    },
    y: {
      ticks: {
        color: "#9A9A9A", font: { size: 10 },
        callback: (v) => fmtK(v),
      },
      grid: { color: "rgba(255,255,255,0.1)" },
    },
  },
});

const dualAxisOpts = (title) => {
  const base = baseChartOpts(title);
  return {
    ...base,
    scales: {
      ...base.scales,
      y: { ...base.scales.y, position: "left" },
      y1: {
        position: "right",
        ticks: { color: "#9A9A9A", font: { size: 10 }, callback: (v) => v + "%" },
        grid: { drawOnChartArea: false },
      },
    },
  };
};

// ==================== Component ====================

function TinyBiweekly() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [trendData, setTrendData] = useState([]);
  const [pools, setPools] = useState([]);
  const [selectedBiweekId, setSelectedBiweekId] = useState("");
  const [clubsData, setClubsData] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(false);
  const [poolFiles, setPoolFiles] = useState(null);

  const [sortConfig, setSortConfig] = useState({ field: "rg_total_rake", dir: "desc" });
  const [filterMode, setFilterMode] = useState("all");

  const [pageMode, setPageMode] = useState("detail");
  const [activeChartTab, setActiveChartTab] = useState("1");

  const [clubHistoryModal, setClubHistoryModal] = useState(false);
  const [clubHistory, setClubHistory] = useState(null);
  const [clubHistoryLoading, setClubHistoryLoading] = useState(false);

  // 雙週獎池分潤規則（rule_type=percentage_of_biweekly_bonus_pool）
  const [bonusPoolRules, setBonusPoolRules] = useState([]);

  // ==================== 技術性調整 (technical adjustment) ====================
  const [clubsVariant, setClubsVariant] = useState("effective"); // 主表檢視版本: effective/original/adjusted
  const [hasAdjusted, setHasAdjusted] = useState(false);          // 本期是否有調整版
  const [currentVariant, setCurrentVariant] = useState("original"); // API 實際回傳的版本
  const [clubsPeriod, setClubsPeriod] = useState(null);          // 該 variant 的期數彙總（上方 KPI 卡跟著切換）
  const [clubsPoolsSummary, setClubsPoolsSummary] = useState(null); // 該 variant 的獎池總覽（明細小表）
  const [adjustmentStale, setAdjustmentStale] = useState(false); // 調整版是否過時（原始資料已更新, #1）
  const [showMttNote, setShowMttNote] = useState(false);         // 常駐「活躍分數計算調整」說明展開狀態
  const [existingAdjustment, setExistingAdjustment] = useState(null); // GET /adjustment 結果
  const [adjustOpen, setAdjustOpen] = useState(false);            // 調整 Modal 開關
  // v2.28.0：water = 從獎池基數扣掉的水量（分 EcoBot / 自營Bot 兩格），取代舊的「直接填總獎池」。
  // pool 保留是為了相容舊的已存調整紀錄（後端 water_deduction 有值時優先）。
  const [adjustForm, setAdjustForm] = useState({ pre: {}, pool: {}, water: {} });
  const [adjustReason, setAdjustReason] = useState("");
  const [simResult, setSimResult] = useState(null);              // 試算結果
  const [simLoading, setSimLoading] = useState(false);
  // v2.28.4 雙向連動：獎池基數（base_water / pool_rate）跟原始總獎池獨立存一份。
  // 存起來是因為它們在整個 Modal 生命週期都不會變，用它們就能純前端即時算
  // 「扣水量 → 總獎池」跟反向的「總獎池 → 該扣多少水」，不必等後端試算回來。
  const [poolBasis, setPoolBasis] = useState(null);              // {base_water, pool_rate}
  const [origTotalPool, setOrigTotalPool] = useState(null);      // 原版總獎池
  // 「調整後總獎池」輸入框：沒 focus 時顯示由扣水量推導的值，focus 時才用 draft，
  // 避免一邊打字一邊被推導值覆寫（兩個 state 互相打架的經典坑）。
  const [totalFocused, setTotalFocused] = useState(false);
  const [totalDraft, setTotalDraft] = useState("");
  const [committing, setCommitting] = useState(false);
  const [origClubs, setOrigClubs] = useState([]);                // 原版 clubs（編輯基準）
  const [notice, setNotice] = useState(null);                    // 送出/還原後的成功提示
  const simTimer = useRef(null);

  // ==================== Data Loading ====================

  useEffect(() => { loadInitialData(); }, []);

  useEffect(() => {
    // 一次性載入所有 active 雙週獎池分潤規則
    axios.get(`${API_BASE}/settlements/special-rules?status=active`)
      .then((res) => {
        if (res.data?.code === 1) {
          const rules = (res.data.data?.rules || [])
            .filter((r) => r.rule_type === 'percentage_of_biweekly_bonus_pool');
          setBonusPoolRules(rules);
        }
      })
      .catch(() => { /* 規則為附註，失敗不影響主功能 */ });
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 不帶 limit ＝ 全部期數（v2.51.3）：期數選單要選得到每一期，
      // 「vs上期」是在 trend 裡往前找一格，trend 一被截斷最舊那期就沒得比。
      const [trendRes, poolsRes] = await Promise.all([
        axios.get(`${API_BASE}/biweekly/trend`),
        axios.get(`${API_BASE}/biweekly/pools`),
      ]);
      if (trendRes.data.code === 1) setTrendData(trendRes.data.data.periods || []);
      if (poolsRes.data.code === 1) {
        const p = poolsRes.data.data.pools || [];
        setPools(p);
        if (p.length > 0) setSelectedBiweekId(p[0].biweek_id);
      }
    } catch (err) {
      setError("載入數據失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBiweekId) loadClubsData(selectedBiweekId, clubsVariant);
  }, [selectedBiweekId, clubsVariant]);

  const loadClubsData = async (biweekId, variant = "effective") => {
    setClubsLoading(true);
    try {
      const [clubsRes, filesRes, adjRes] = await Promise.all([
        axios.get(`${API_BASE}/biweekly/clubs/${biweekId}`, { params: { variant } }),
        axios.get(`${API_BASE}/biweekly/files/${biweekId}`),
        axios.get(`${API_BASE}/biweekly/${biweekId}/adjustment`),
      ]);
      if (clubsRes.data.code === 1) {
        const d = clubsRes.data.data;
        setClubsData(d.clubs || []);
        setHasAdjusted(!!d.has_adjusted);
        setCurrentVariant(d.variant || "original");
        setClubsPeriod(d.period || null);
        setClubsPoolsSummary(d.pools_summary || null);
        setAdjustmentStale(!!d.adjustment_stale);
      }
      if (filesRes.data.code === 1) setPoolFiles(filesRes.data.data);
      setExistingAdjustment(adjRes.data.code === 1 ? adjRes.data.data : null);
    } catch (err) {
      console.error("載入俱樂部明細失敗:", err);
    } finally {
      setClubsLoading(false);
    }
  };

  // ==================== 技術性調整 handlers ====================

  const runSimulate = useCallback((form) => {
    if (!selectedBiweekId) return;
    if (simTimer.current) clearTimeout(simTimer.current);
    simTimer.current = setTimeout(async () => {
      setSimLoading(true);
      try {
        const body = buildOverrides(form);
        const res = await axios.post(`${API_BASE}/biweekly/${selectedBiweekId}/simulate`, body);
        if (res.data.code === 1) {
          const d = res.data.data;
          setSimResult(d);
          if (d.pool_basis?.base_water > 0) setPoolBasis(d.pool_basis);
          if (d.delta?.total_pool?.original != null) setOrigTotalPool(d.delta.total_pool.original);
        }
      } catch (e) {
        console.error("試算失敗", e);
      } finally {
        setSimLoading(false);
      }
    }, 300);
  }, [selectedBiweekId]);

  /**
   * 開啟技術性調整 Modal。
   * @param {{prefillSelfBot?: number}} opts 告警條那顆按鈕會帶 prefillSelfBot（= 自營Bot 本期水量）。
   *
   * v2.28.4：以前是 `openAdjustModal(); setTimeout(() => updateWater(...), 400)`，
   * 那個 setTimeout 抓的是點擊當下的 adjustForm 閉包，400ms 後 Modal 已經被
   * API 結果覆寫過 → 預填時好時壞，而且會把既有的 EcoBot 扣除值和水前輸贏
   * override 一起清空。改成用參數在同一次 setState 裡塞進去。
   */
  const openAdjustModal = async (opts = {}) => {
    const initForm = { pre: {}, pool: {}, water: {} };
    const a = existingAdjustment?.adjustment;
    if (a) {
      Object.entries(a.pre_rake_pl_overrides || {}).forEach(([cid, ov]) => { initForm.pre[cid] = ov.adjusted; });
      if (a.water_deduction) {
        // v2.28.0 新式：還原兩格扣水量
        initForm.water.ecobot = a.water_deduction.ecobot ?? "";
        initForm.water.self_bot = a.water_deduction.self_bot ?? "";
      } else {
        // 舊式紀錄：pool override 以「總獎池」單一數字表示（= 輸退 + 活躍 調整後）
        const r = a.pool_overrides?.rebate_pool_total?.adjusted;
        const ac = a.pool_overrides?.activity_pool_total?.adjusted;
        if (r != null && ac != null) initForm.pool.total = r + ac;
      }
    }
    // 只在本期還沒有調整版時自動帶入建議值。已經存過的調整（新式 water_deduction
    // 或舊式 pool_overrides）一律原樣還原 —— 否則一打開 Modal，總獎池就跟已送出的
    // 版本對不起來，等於默默竄改已結的帳。
    if (!a && opts.prefillSelfBot > 0) {
      initForm.water.self_bot = String(Math.round(opts.prefillSelfBot * 100) / 100);
    }
    setAdjustForm(initForm);
    setAdjustReason(a?.reason || "");
    setSimResult(null);
    setTotalFocused(false);
    setAdjustOpen(true);
    // 載入原版 clubs 當編輯基準
    try {
      const res = await axios.get(`${API_BASE}/biweekly/clubs/${selectedBiweekId}`, { params: { variant: "original" } });
      if (res.data.code === 1) setOrigClubs(res.data.data.clubs || []);
    } catch (e) { /* ignore */ }
    runSimulate(initForm);
  };

  const updatePre = (cid, value) => {
    const f = { ...adjustForm, pre: { ...adjustForm.pre, [cid]: value } };
    setAdjustForm(f); runSimulate(f);
  };
  const updateWater = (key, value) => {
    const f = { ...adjustForm, water: { ...(adjustForm.water || {}), [key]: value } };
    setAdjustForm(f); runSimulate(f);
  };

  // ==================== v2.28.4 雙向連動 ====================
  // 唯一的式子：總獎池 = (基數水量 − EcoBot扣除 − 自營Bot扣除) × 池率
  // 兩個方向都套它，所以不管 Brad 從哪一邊輸入，存進 DB 的永遠是 water_deduction
  // （兩類各扣多少的紀錄不會消失）。
  const dedNum = (v) => (v === "" || v == null || isNaN(Number(v)) ? 0 : Number(v));

  // 有沒有真的填了扣水量（空字串不算）—— 決定走新式還是舊式，跟 buildOverrides 同一個判準
  const waterFilled = ["ecobot", "self_bot"].some((k) => {
    const v = adjustForm.water?.[k];
    return v !== "" && v != null && !isNaN(Number(v));
  });

  const derivedTotalPool = useMemo(() => {
    if (waterFilled) {
      if (!poolBasis || !(poolBasis.pool_rate > 0)) return null;
      const ded = dedNum(adjustForm.water?.ecobot) + dedNum(adjustForm.water?.self_bot);
      return Math.max(0, (poolBasis.base_water - ded) * poolBasis.pool_rate);
    }
    // 舊式紀錄（pool_overrides，沒有 water_deduction）：總獎池就是存起來的那個數字。
    // 不走這條的話，打開歷史調整會顯示成「原始總獎池」，看起來像沒調整過。
    const t = adjustForm.pool?.total;
    if (t !== "" && t != null && !isNaN(Number(t))) return Number(t);
    if (!poolBasis || !(poolBasis.pool_rate > 0)) return null;
    return poolBasis.base_water * poolBasis.pool_rate;   // 完全沒調整 = 原始總獎池
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolBasis, adjustForm.water, adjustForm.pool, waterFilled]);

  // 目標總獎池打到「EcoBot 扣除已經是 0」還達不到 → 夾住，並在畫面上警告
  const targetClamped = totalFocused && totalDraft !== "" && !isNaN(Number(totalDraft))
    && derivedTotalPool != null && Math.abs(Number(totalDraft) - derivedTotalPool) > 0.01;

  /**
   * 反向輸入：填「調整後總獎池」→ 反推 EcoBot 該扣多少水。
   * 自營Bot 那格不動（它有明確建議值 = 本期自營Bot 貢獻的水），差額全算在 EcoBot 頭上 ——
   * 這跟後端「獎池縮小額全部硬扣在 EcoBot 那一列」的既有口徑一致。
   */
  const updateTargetTotal = (raw) => {
    setTotalDraft(raw);
    if (!poolBasis || !(poolBasis.pool_rate > 0)) return;
    let ecoStr = "";
    if (raw !== "" && raw != null && !isNaN(Number(raw))) {
      const sb = dedNum(adjustForm.water?.self_bot);
      const need = poolBasis.base_water - sb - Number(raw) / poolBasis.pool_rate;
      ecoStr = String(Math.max(0, Math.round(need * 100) / 100));
    }
    // pool 一併清掉：從總獎池這格輸入就代表改走新式(water_deduction)，
    // 留著舊式的 pool.total 會在清空這格時默默跳回舊值。
    const f = { ...adjustForm, pool: {}, water: { ...(adjustForm.water || {}), ecobot: ecoStr } };
    setAdjustForm(f); runSimulate(f);
  };
  const buildOverrideBody = () => buildOverrides(adjustForm);

  // 帶 JWT 的檔案下載（window.open 不會帶 token → auth_required，故用 axios blob）
  const downloadFile = async (url, fallbackName) => {
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = m?.[1] || fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError("下載失敗: " + (e.response?.status === 401 ? "未授權（請重新登入）" : (e.message || "")));
    }
  };

  const handleCommit = async () => {
    setCommitting(true);
    try {
      const body = { ...buildOverrideBody(), reason: adjustReason, recorded_by: "admin" };
      const res = await axios.post(`${API_BASE}/biweekly/${selectedBiweekId}/adjustment/commit`, body);
      if (res.data.code === 1) {
        const d = res.data.data || {};
        setAdjustOpen(false);
        setClubsVariant("adjusted");
        await loadClubsData(selectedBiweekId, "adjusted");
        loadInitialData();
        setNotice(
          `✓ 送出完成並已重跑：` +
          `調整版獎池已寫入、` +
          `調整版 Excel ${d.excel?.generated ? "已產生" : "未產生"}、` +
          `營收數據(week2) ${d.revenue_refreshed ? "已重算" : "未重算"}。` +
          `可用上方「調整版整包」下載新的 ZIP。`
        );
      } else {
        alert("送出失敗: " + (res.data.msg || ""));
      }
    } catch (e) {
      alert("送出失敗: " + e.message);
    } finally {
      setCommitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("確定還原為原版？將刪除本期調整版資料與 Excel，並重跑營收。")) return;
    setCommitting(true);
    try {
      const res = await axios.delete(`${API_BASE}/biweekly/${selectedBiweekId}/adjustment`);
      if (res.data.code === 1) {
        setAdjustOpen(false);
        setClubsVariant("effective");
        await loadClubsData(selectedBiweekId, "effective");
        loadInitialData();
        setNotice("✓ 已還原為原版：調整版資料與 Excel 已刪除、營收數據(week2) 已重算回原值。");
      }
    } catch (e) {
      alert("還原失敗: " + e.message);
    } finally {
      setCommitting(false);
    }
  };

  const openClubHistory = async (clubId) => {
    setClubHistoryModal(true);
    setClubHistoryLoading(true);
    setClubHistory(null);
    try {
      const res = await axios.get(`${API_BASE}/biweekly/club-history/${clubId}`);
      if (res.data.code === 1) setClubHistory(res.data.data);
    } catch (err) {
      console.error("載入俱樂部歷史失敗:", err);
    } finally {
      setClubHistoryLoading(false);
    }
  };

  // ==================== Derived Data ====================

  // 🔴 選了某期但 trend 裡沒有 → 回 null，**不要拿別期頂替**（v2.51.2）。
  //    原本退回 trend 最後一筆：trend 一度只回最舊的 20 期，最新一期不在裡面，
  //    結果「vs上期」比到上上期、主表佔比用了上一期的總額，數字看起來合理卻是錯的。
  //    寧可不顯示也不要顯示錯的。還沒選期（首次載入）才取最新一筆。
  const selectedPeriod = useMemo(() => {
    if (selectedBiweekId) return trendData.find((p) => p.biweek_id === selectedBiweekId) || null;
    return trendData[trendData.length - 1] || null;
  }, [trendData, selectedBiweekId]);

  const selectedPool = useMemo(() =>
    pools.find((p) => p.biweek_id === selectedBiweekId) || null,
    [pools, selectedBiweekId]
  );

  // 活躍分數 MTT 補分 BUG（v2.9.2 修正）標記：
  // 第一個「已修復」期數（biweek_id 最小者）→ 顯示一次性「已修復」標；其餘修復期不標。
  const firstFixedBiweekId = useMemo(() => {
    const fixed = pools
      .filter((p) => p.active_score_mtt_status === "fixed")
      .map((p) => p.biweek_id);
    return fixed.length ? fixed.sort()[0] : null;
  }, [pools]);

  // 目前選中期數的 MTT 計算狀態（'affected' | 'fixed' | null）
  const selectedMttStatus = useMemo(() => {
    const p = pools.find((x) => x.biweek_id === selectedBiweekId)
      || trendData.find((x) => x.biweek_id === selectedBiweekId);
    return p ? p.active_score_mtt_status : null;
  }, [pools, trendData, selectedBiweekId]);

  // 上方 KPI 卡跟著「原版/調整版」toggle：優先用該 variant 的期數彙總(clubsPeriod)，
  // 沒有才退回 trend(effective)。確保切換時 sum 區數字一起變。
  const viewPeriod = useMemo(() =>
    (clubsPeriod && clubsPeriod.biweek_id === selectedBiweekId) ? clubsPeriod : selectedPeriod,
    [clubsPeriod, selectedPeriod, selectedBiweekId]
  );

  // v2.28.4：自營Bot 這期的貢獻列拉到元件層 —— KPI 區、告警條、技術性調整 Modal
  // 三個地方都要用「建議扣除水量」，以前只在 KPI 區的 IIFE 裡算，Modal 拿不到。
  const selfBotRow = useMemo(() =>
    (viewPeriod?.contribution_summary?.rows || []).find((r) => r.label === "自營Bot") || {},
    [viewPeriod]
  );

  const prevPeriod = useMemo(() => {
    if (!selectedPeriod) return null;
    const idx = trendData.findIndex((p) => p.biweek_id === selectedPeriod.biweek_id);
    return idx > 0 ? trendData[idx - 1] : null;
  }, [trendData, selectedPeriod]);

  // ==================== Sorting & Filtering ====================

  const handleSort = useCallback((field) => {
    setSortConfig((prev) => ({
      field,
      dir: prev.field === field && prev.dir === "desc" ? "asc" : "desc",
    }));
  }, []);

  const sortedClubs = useMemo(() => {
    let data = [...clubsData];
    if (filterMode === "bot") data = data.filter((c) => c.tag === "bot");
    else if (filterMode === "selfbot") data = data.filter((c) => c.tag === "selfbot");
    else if (filterMode === "hq") data = data.filter((c) => c.tag === "hq");
    else if (filterMode === "external") data = data.filter((c) => c.tag === "external");
    else if (filterMode === "qualified") data = data.filter((c) => {
      const e = c.eligibility || {};
      return e.rebate_pool || e.activity_pool;
    });
    else if (filterMode === "rebate") data = data.filter((c) => c.rebate_snapshot && c.rebate_snapshot.rate != null);
    else if (filterMode === "pure_share") data = data.filter((c) => !!c.pool_mode_snapshot);

    const { field, dir } = sortConfig;
    data.sort((a, b) => {
      let va, vb;
      if (field === "pool_total") {
        va = a.pool_total || 0;
        vb = b.pool_total || 0;
      } else if (field === "rg_total_rake" || field === "pre_rake_pl_exclude_jp" || field === "active_score") {
        va = (a.data || {})[field] || 0;
        vb = (b.data || {})[field] || 0;
      } else if (field === "rebate_pool" || field === "activity_pool" || field === "main_pool") {
        va = (a.allocation || {})[field] || 0;
        vb = (b.allocation || {})[field] || 0;
      } else if (field === "club_name") {
        va = a.club_name || "";
        vb = b.club_name || "";
        return dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      } else {
        va = a[field] || 0;
        vb = b[field] || 0;
      }
      return dir === "asc" ? va - vb : vb - va;
    });
    return data;
  }, [clubsData, sortConfig, filterMode]);

  const tableTotals = useMemo(() => {
    const t = { rg: 0, pl: 0, rebate: 0, score: 0, activity: 0, volume: 0, total: 0 };
    clubsData.forEach((c) => {
      t.rg += (c.data || {}).rg_total_rake || 0;
      t.pl += (c.data || {}).pre_rake_pl_exclude_jp || 0;
      t.rebate += (c.allocation || {}).rebate_pool || 0;
      t.score += (c.data || {}).active_score || 0;
      t.activity += (c.allocation || {}).activity_pool || 0;
      t.volume += (c.allocation || {}).main_pool || 0;
      t.total += c.pool_total || 0;
    });
    return t;
  }, [clubsData]);

  // ==================== Chart Data ====================

  const chartLabels = useMemo(() => trendData.map((p) => p.label), [trendData]);

  const poolTrendChartData = useMemo(() => ({
    labels: chartLabels,
    datasets: [
      {
        label: "獎池總額", data: trendData.map((p) => p.total),
        borderColor: "#fff", backgroundColor: "rgba(255,255,255,0.06)",
        tension: 0.3, pointRadius: 6, pointHoverRadius: 8,
        borderWidth: 3, fill: true, order: 0,
      },
      {
        label: "輸退獎池", data: trendData.map((p) => p.rebate_pool),
        borderColor: CHART_COLORS.rebate, backgroundColor: "transparent",
        tension: 0.3, pointRadius: 3, borderWidth: 1.5, borderDash: [6, 3], order: 1,
      },
      {
        label: "活躍獎池", data: trendData.map((p) => p.activity_pool),
        borderColor: CHART_COLORS.activity, backgroundColor: "transparent",
        tension: 0.3, pointRadius: 3, borderWidth: 1.5, borderDash: [6, 3], order: 2,
      },
      ...(trendData.some((p) => p.volume_pool > 0) ? [{
        label: "水量獎池", data: trendData.map((p) => p.volume_pool),
        borderColor: CHART_COLORS.volume, backgroundColor: "transparent",
        tension: 0.3, pointRadius: 3, borderWidth: 1.5, borderDash: [3, 3], order: 3,
      }] : []),
    ],
  }), [trendData, chartLabels]);

  const waterChartData = useMemo(() => {
    const calcEcoPool = (p) => {
      if (p.ecobot_pool != null) return p.ecobot_pool;  // 後端硬扣模型（與明細頁一致）
      const rate = p.exclude_aof ? 0.13 : 0.20;
      const ecoWater = p.exclude_aof ? p.ecobot_rg_rake : (p.ecobot_rg_rake + (p.ecobot_aof_rake || 0));
      return ecoWater * rate;
    };
    return {
      labels: chartLabels,
      datasets: [
        {
          type: "bar", label: "EcoBot 產生獎池",
          data: trendData.map(calcEcoPool),
          backgroundColor: "rgba(253, 93, 147, 0.7)", stack: "water",
        },
        {
          type: "bar", label: "真人產生獎池",
          data: trendData.map((p) => p.total - calcEcoPool(p)),
          backgroundColor: "rgba(31, 142, 241, 0.7)", stack: "water",
        },
        {
          type: "line", label: "EcoBot 獎池佔比 %",
          data: trendData.map((p) => {
            if (p.total <= 0) return 0;
            return +((calcEcoPool(p) / p.total) * 100).toFixed(1);
          }),
          borderColor: "#ffd600", backgroundColor: "transparent",
          tension: 0.3, pointRadius: 4, borderWidth: 2, yAxisID: "y1",
        },
      ],
    };
  }, [trendData, chartLabels]);

  const hqRecoveryChartData = useMemo(() => ({
    labels: chartLabels,
    datasets: [
      {
        type: "bar", label: "本部回收",
        data: trendData.map((p) => p.hq_hq_return),
        backgroundColor: "rgba(31, 142, 241, 0.7)", stack: "recovery",
      },
      {
        type: "bar", label: "EcoBot 俱樂部回收",
        data: trendData.map((p) => p.hq_eco_return),
        backgroundColor: "rgba(0, 214, 180, 0.7)", stack: "recovery",
      },
      {
        type: "line", label: "回收佔比 %",
        data: trendData.map((p) => p.total > 0 ? +(p.hq_total_return / p.total * 100).toFixed(1) : 0),
        borderColor: "#e14eca", backgroundColor: "transparent",
        tension: 0.3, pointRadius: 4, yAxisID: "y1",
      },
      {
        type: "line", label: "EcoBot 水前輸贏（成本）",
        data: trendData.map((p) => p.ecobot_pre_rake_pl),
        borderColor: CHART_COLORS.botCost, backgroundColor: "transparent",
        tension: 0.3, pointRadius: 4, borderDash: [5, 5],
      },
    ],
  }), [trendData, chartLabels]);

  const clubShareChartData = useMemo(() => {
    if (trendData.length === 0) return { labels: [], datasets: [] };
    const shareColors = ["#1f8ef1", "#00d6b4", "#fd5d93", "#f17e5d", "#e14eca", "#9A9A9A"];

    const clubTotalsAcross = {};
    trendData.forEach((p) => {
      const cpt = p.club_pool_totals || {};
      Object.entries(cpt).forEach(([cid, info]) => {
        if (!clubTotalsAcross[cid]) clubTotalsAcross[cid] = { name: info.name, sum: 0 };
        clubTotalsAcross[cid].sum += info.total || 0;
      });
    });

    const sorted = Object.entries(clubTotalsAcross)
      .sort((a, b) => b[1].sum - a[1].sum);
    const top5 = sorted.slice(0, 5);
    const top5Ids = new Set(top5.map(([id]) => id));

    const datasets = top5.map(([cid, info], i) => ({
      label: info.name || cid,
      data: trendData.map((p) => {
        const cpt = p.club_pool_totals || {};
        return (cpt[cid] && cpt[cid].total) || 0;
      }),
      backgroundColor: shareColors[i] + "cc",
      borderColor: shareColors[i],
      borderWidth: 1, stack: "clubs",
    }));

    datasets.push({
      label: "其他",
      data: trendData.map((p) => {
        const cpt = p.club_pool_totals || {};
        let others = 0;
        Object.entries(cpt).forEach(([cid, info]) => {
          if (!top5Ids.has(cid)) others += info.total || 0;
        });
        return others;
      }),
      backgroundColor: "rgba(154, 154, 154, 0.35)",
      borderColor: "#9A9A9A",
      borderWidth: 1, stack: "clubs",
    });

    return { labels: chartLabels, datasets };
  }, [trendData, chartLabels]);

  // ==================== Render Helpers ====================

  const COL_ALIGN = {
    club_id: 'left', club_name: 'left', active_score: 'center',
  };
  const SortHeader = ({ field, label, suffix }) => {
    const active = sortConfig.field === field;
    const align = COL_ALIGN[field] || 'right';
    return (
      <th
        style={{ ...thStyle, cursor: "pointer", userSelect: "none", textAlign: align }}
        onClick={() => handleSort(field)}
      >
        {label}{suffix}{" "}
        <span style={{ color: active ? '#667eea' : 'rgba(255,255,255,0.3)', fontSize: '10px' }}>
          {active ? (sortConfig.dir === "desc" ? "\u25BC" : "\u25B2") : "\u25BC"}
        </span>
      </th>
    );
  };

  const TagBadge = ({ tag }) => {
    if (tag === "bot") return (
      <span style={{
        background: S.botGrad, color: '#fff', fontSize: '10px', fontWeight: 600,
        padding: '2px 8px', borderRadius: '10px', letterSpacing: '0.5px',
      }}>EcoBot</span>
    );
    if (tag === "hq") return (
      <span style={{
        background: S.hqGrad, color: '#fff', fontSize: '10px', fontWeight: 600,
        padding: '2px 8px', borderRadius: '10px', letterSpacing: '0.5px',
      }}>本部</span>
    );
    // v2.28.0 我方自營的開桌機器人（≠ EcoBot，EcoBot 是外部夥伴）
    if (tag === "selfbot") return (
      <span style={{
        background: 'linear-gradient(135deg,#00d6b4,#0d9488)', color: '#fff', fontSize: '10px',
        fontWeight: 600, padding: '2px 8px', borderRadius: '10px', letterSpacing: '0.5px',
      }}>自營Bot</span>
    );
    return null;
  };

  const StatusBadge = ({ qualified }) => (
    <span style={{
      background: qualified ? S.successGrad : 'rgba(149,165,166,0.3)',
      color: '#fff', fontSize: '10px', fontWeight: 600,
      padding: '3px 10px', borderRadius: '10px',
    }}>
      {qualified ? "達標" : "未達標"}
    </span>
  );

  const fmtDeltaK = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1e6) return (v / 1e6).toFixed(2) + "M";
    if (abs >= 1e3) return (v / 1e3).toFixed(0) + "K";
    return v.toFixed(0);
  };

  /**
   * 期別差額。
   *
   * `plain` = 表格用的樸素版：只有 ▲/▼ + 數字（+ 百分比），**不畫徽章底色**。
   * ⚠️ 徽章那一套是給 KPI 卡用的（一格裡只有一個數字，需要把它跟主數字分開）；
   *    放進表格的一整欄會變成每一列都有一塊色塊，噪音大於資訊
   *   （Brad 2026-09-10：「不用用徽章吧？直接顯示上箭頭或下箭頭，後面帶數字跟 %」）。
   * `pct` 給了就在數字後面補上變化百分比。
   */
  const DeltaSpan = ({ value, isPct = false, plain = false, pct = null }) => {
    if (value === null || value === undefined) return null;
    const isUp = value > 0;
    const color = isUp ? "#69f0ae" : value < 0 ? "#ff8a80" : "rgba(255,255,255,0.4)";
    const prefix = isUp ? "▲ +" : value < 0 ? "▼ " : "";
    const display = isPct ? value.toFixed(1) + "%" : fmtDeltaK(value);
    if (plain) {
      return (
        <span style={{ color, fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
          {prefix}{display}
          {pct !== null && pct !== undefined && Number.isFinite(pct) && (
            <span style={{ opacity: 0.75, marginLeft: 6 }}>
              ({isUp ? '+' : ''}{pct.toFixed(1)}%)
            </span>
          )}
        </span>
      );
    }
    return (
      /* 🔴 字級用**絕對值**不用 `em`。
            舊版是 `0.48em`，而這個徽章出現在四種不同字級的父層底下
           （主卡 2.4rem、子卡主值 22px、子卡副值 14px、表格 13px），
            於是同一個徽章在畫面上有四種大小 —— Brad 2026-09-10 一眼看出來。
            改成固定 12px：等於原本最大的那一種（22px × 0.48 ≈ 10.6px）再大一點。 */
      <span style={{
        color, marginLeft: 8, fontWeight: 600, fontSize: 12,
        background: isUp ? 'rgba(105,240,174,0.14)' : value < 0 ? 'rgba(255,138,128,0.14)' : 'transparent',
        padding: '3px 9px', borderRadius: 5, verticalAlign: 'middle',
        whiteSpace: 'nowrap', lineHeight: 1.25,
      }}>
        {prefix}{display} <span style={{ fontSize: 10.5, opacity: 0.72 }}>vs上期</span>
      </span>
    );
  };

  /**
   * 獎池摘要的兩個新元件（v2.35.0）
   *
   * 改版前這一區是 8~10 張各自獨立的 KpiCard，每張 80~110px 高、下面各自
   * 再留 16px margin，攤在兩排 Bootstrap 欄位裡 —— 光是摘要就吃掉 300px 以上，
   * 而且「獎池總額 = 輸退 + 活躍」這種明確的從屬關係，在版面上完全看不出來
   *（三張同樣大小的卡並排，讀起來像三個不相干的指標）。
   *
   * 新結構照資料本身的層次來排：
   *
   *   PoolHero    總額（大）│ 輸退 │ 活躍      ← 一張橫幅，從屬關係用分隔線表達
   *   PoolPanel   我方回收 / EcoBot / 自營Bot  ← 三個同層級的面板
   *
   * ⚠️ **每一個數字、每一個 vs上期、每一個達標與附註都保留**，只是換排法。
   *
   * 視覺上刻意「一重三輕」：Hero 保留漸層（它是標題數字），三個面板改成
   * 平面底色 + 頂端一條該類別的顏色 —— 四張都用漸層會互相搶，那正是舊版
   * 看起來很重的原因。
   */
  const HeroStat = ({ label, value, delta, deltaPct, sub, big = false }) => (
    <div style={{ minWidth: 0, flex: big ? '1 1 220px' : '1 1 150px' }}>
      <div style={{
        color: 'rgba(255,255,255,0.72)', fontSize: 11, fontWeight: 500,
        letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4,
      }}>{label}</div>
      <div style={{
        fontSize: big ? 30 : 21, fontWeight: 700, color: '#fff', lineHeight: 1.15,
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
      }}>
        {value}
        <DeltaSpan value={delta} isPct={deltaPct} />
      </div>
      {sub && (
        <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: 11, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );

  const PoolHero = ({ children }) => (
    <div style={{
      background: KPI_GRADIENTS[0].grad,
      boxShadow: KPI_GRADIENTS[0].shadow,
      borderRadius: 10, padding: '16px 20px', marginBottom: 14,
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 20,
    }}>
      {children}
    </div>
  );

  /** 三個同層級面板。primary 是主數字，secondary 是它的佔比／配對數字。 */
  const PoolPanel = ({ accent, title, primary, secondary, detail }) => (
    /* 子卡：**整卡上一層類別色**、不畫任何線條（Brad 2026-09-10 選定）。
       ⚠️ 濃度是 `2e`（≈18%）不是原本評估版的 `0d`（5%）—— 5% 在這個深色主題上
          幾乎看不出來，Brad 回報「太淡」。邊框同色 80（50%）讓三張卡彼此分得開。
       🚫 不要再加頂端／左側色條 —— 底色已經在講同一件事，加線只是重複。 */
    <div style={{
      background: `${accent}2e`,
      border: `1px solid ${accent}80`,
      borderRadius: 10, padding: '13px 16px', height: '100%',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{
        color: 'rgba(255,255,255,0.72)', fontSize: 11, fontWeight: 600,
        letterSpacing: '0.5px', textTransform: 'uppercase',
      }}>{title}</div>

      {/* 🔴 這裡**不能用 `flex-wrap: wrap` 的橫向排列**。
             wrap 只在「放不下」時才換行，於是同一個版型會因為內容寬度長出兩種結果：
             我方回收 / EcoBot 的主值帶著 vs上期 徽章夠寬，把佔比擠到第二行；
             自營Bot 是 `0`、又沒有徽章，兩段就併在同一行
            （Brad 2026-09-10：「為什麼只有自營 BOT 那張沒換行？」）。
          → 改成 column，主值與副值**永遠各佔一行**，三張卡結構一致，
             跟數字多長、有沒有 delta 都無關。 */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: S.textPrimary, lineHeight: 1.15,
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          {primary.value}
          <DeltaSpan value={primary.delta} isPct={primary.isPct} />
        </div>
        {/* ⚠️ 副值在濃色底上不能再用 accent 本色（同色系疊同色系會糊）→ 用白字。
               🔴 這個註解要放在 `{secondary && (` **外面**：JSX 註解本身是一個運算式，
                  放進 `&&` 的括號裡會變成「兩個相鄰的運算式」而語法錯誤。
               🔴 而且註解**內文不能出現註解結束符號**（星號加斜線）—— 我上一版
                  在說明文字裡寫了那兩個字元，註解被提早關掉，後半段整段變成
                  畫面上的可見文字，三張卡都印出一行亂碼。 */}
        {secondary && (
          <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', lineHeight: 1.3,
                        display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500, fontSize: 11, marginRight: 5 }}>
              {secondary.label}
            </span>
            {secondary.value}
            <DeltaSpan value={secondary.delta} isPct={secondary.isPct} />
          </div>
        )}
      </div>

      {detail && (
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11, lineHeight: 1.5, marginTop: 'auto' }}>
          {detail}
        </div>
      )}
    </div>
  );

  const FilterBtn = ({ fKey, label }) => {
    const isActive = filterMode === fKey;
    const fs = FILTER_STYLES[fKey] || FILTER_STYLES.all;
    return (
      <button
        onClick={() => setFilterMode(fKey)}
        style={{
          background: isActive ? fs.activeBg : fs.bg,
          border: isActive ? 'none' : `1px solid ${fs.border}`,
          color: isActive ? '#fff' : fs.color,
          padding: '5px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
          cursor: 'pointer', marginRight: 6, transition: 'all 0.2s',
          boxShadow: isActive ? `0 3px 8px rgba(0,0,0,0.2)` : 'none',
        }}
      >
        {label}
      </button>
    );
  };

  // ==================== Render ====================

  if (loading) {
    return (
      <div className="content">
        <TinyLoading variant="page" text="載入中" />
      </div>
    );
  }

  return (
    <div className="content">
      {/*
        身分 / 範圍 / 視角三者分開，交給公版排列（見 components/tiny/TinyPageHeader）。

        改動前這一區是「標題 ＋ 說明 ＋ 期數選擇器 ＋ 檢視切換」擠在同一條 flex row，
        所以每加一個東西就得重排一次，也是 23 個 Tiny 頁面 header 各長各樣的原因。

        ⚠️ 期數選擇器仍然只在 detail 模式出現 —— trend 模式是跨期趨勢，沒有「哪一期」
           可選，硬留著會變成一個按了沒反應的控制項。
      */}
      <TinyPageHeader
        title="雙週獎池分析"
        /* ⚠️ 獎池模式（二元/三元、含不含 AOF、幾家參與）本來是內容區一條 43px 的
              橫幅。它是**這期資料的身分**不是數據，所以收進 header ——
              badge 放模式與 AOF 口徑，公式與家數放 description。 */
        /* ⚠️ 這裡用 `viewPeriod` 不是 `selectedPeriod` —— 原本那條橫幅在 Zone A 裡面，
              讀的是跟著「原版／調整版」toggle 走的那一份。header 也必須跟著切，
              否則切到調整版時 badge 還停在原版的口徑。 */
        badges={pageMode === "detail" && viewPeriod ? [
          { text: viewPeriod.pool_type, tone: viewPeriod.pool_type === "三元" ? "info" : "success" },
          { text: viewPeriod.exclude_aof ? "排除 AOF" : "含 AOF",
            tone: viewPeriod.exclude_aof ? "warning" : "success" },
        ] : []}
        description={pageMode === "detail" && viewPeriod
          ? `${viewPeriod.exclude_aof ? "獎池 = (NLH + PLO) × 13%" : "獎池 = (NLH + PLO + AOF) × 20%"}　·　${viewPeriod.clubs_count} 個俱樂部參與`
          : "由報表整合中心雙數周流程自動生成"}
        /* 期數選擇器只在「期數明細」有 → 保留該列高度，切 tab 時 header 不變高 */
        reserveScope
        scope={
          pageMode === "detail" ? (
            <TinyWeekSelector
              label=""
              options={pools}
              value={selectedBiweekId}
              onChange={setSelectedBiweekId}
              getOptionValue={(p) => p.biweek_id}
              getOptionKey={(p) => p.biweek_id}
              prevLabel="上期"
              nextLabel="下期"
              showMeta={false}
              renderOptionLabel={(p) => {
                if (!p.week1_id || !p.week2_id) return p.biweek_id;
                const w1 = p.week1_id;
                const w2End = p.week2_id.split("-")[1] || "";
                const y1 = w1.slice(0, 4);
                const y2 = w2End.slice(0, 4);
                const start = `${w1.slice(4, 6)}/${w1.slice(6, 8)}`;
                const end = `${w2End.slice(4, 6)}/${w2End.slice(6, 8)}`;
                return y1 === y2 ? `${y1} ${start} - ${end}` : `${y1}/${start} - ${y2}/${end}`;
              }}
            />
          ) : null
        }
        tabs={{
          items: [
            { key: "detail", label: "期數明細", icon: "fa fa-list-ul" },
            { key: "trend", label: "趨勢總覽", icon: "fa fa-line-chart" },
          ],
          active: pageMode,
          onChange: setPageMode,
        }}
      />

      {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
      {notice && <Alert color="success" toggle={() => setNotice(null)}>{notice}</Alert>}
      {adjustmentStale && (
        <Alert color="warning">
          ⚠ 本期原始資料在調整之後又被重新產生過，目前的「調整版」可能已過時（基於舊資料）。
          建議「技術性調整 → 還原為原版」後重新調整一次。
        </Alert>
      )}

      {/* ===== 活躍分數 MTT 補分 BUG（v2.9.2 修正）的說明 =====
           ⚠️ 原本是**常駐**的，但它只跟兩種期數有關：受影響的舊期（⚠ MTT BUG）、
              以及第一個修好的期（✓ 已修復）。其餘期數看到它只是雜訊，而且它就擋在
              KPI 前面（Brad 2026-09-10：「有被影響的那幾期再顯示這塊就好了」）。
           🔑 判斷條件跟下面那兩個 badge **用同一組來源**（selectedMttStatus /
              firstFixedBiweekId），不要另外寫一套，否則會漂掉。 */}
      {(selectedMttStatus === 'affected'
        || (selectedMttStatus === 'fixed' && selectedBiweekId === firstFixedBiweekId)) && (
      <div style={{
        background: S.cardBg, border: `1px solid ${S.border}`, borderRadius: 8,
        padding: '8px 14px', marginBottom: 16, fontSize: 13,
      }}>
        <div
          onClick={() => setShowMttNote((v) => !v)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: S.textPrimary }}
        >
          <span><span role="img" aria-label="info">ℹ️</span> 關於活躍分數計算調整（2026-06 / v2.9.2）</span>
          <span style={{ color: S.textMuted, fontSize: 12 }}>{showMttNote ? '▲ 收合' : '▼ 展開'}</span>
        </div>
        {showMttNote && (
          <div style={{ marginTop: 8, color: S.textMuted, lineHeight: 1.7 }}>
            修正了一個長期存在的計算問題：RG 手數未達標、但靠參加 MTT（含 rebuy 共 ≥4 次）補分的玩家，
            其活躍分數先前未被計入，導致部分俱樂部活躍分數偏低、活躍獎池分配偏少（少分的金額被重新分配給其他俱樂部）。
            此問題自雙週獎池上線即存在，<b>只影響活躍獎池</b>（輸退獎池 / 水量 / 水前輸贏不受影響）。
            <br />
            期數選單與下方標示說明：<b style={{ color: '#d9a400' }}>⚠ MTT BUG</b> = 受影響的歷史結算
            （<b>未回溯重算，數字維持當時結算原樣</b>）；<b style={{ color: '#1f9d55' }}>✓ 已修復</b> = 自該期起已套用修正。
          </div>
        )}
      </div>
      )}

      {/* ============================================================ */}
      {/* ===== MODE: Detail (期數明細) ===== */}
      {/* ============================================================ */}

      {/* ===== Zone A: KPI Summary (detail mode only) ===== */}
      {pageMode === "detail" && viewPeriod && (() => {
        const selectedPeriod = viewPeriod;  // 跟著「原版/調整版」toggle，sum 區數字一起切換
        const isTriPool = selectedPeriod.pool_type === "三元";

        const recoveryPct = selectedPeriod.total > 0
          ? (selectedPeriod.hq_total_return / selectedPeriod.total * 100) : 0;
        const prevRecoveryPct = prevPeriod && prevPeriod.total > 0
          ? (prevPeriod.hq_total_return / prevPeriod.total * 100) : null;

        // EcoBot 產生獎池：採「硬扣模型」，由後端算好（技術性調整的總獎池縮小額全部從這裡扣）。
        // 後端 period.ecobot_pool / ecobot_pool_pct 已含此邏輯；前端直接顯示。
        const ecoWater = selectedPeriod.exclude_aof
          ? selectedPeriod.ecobot_rg_rake
          : (selectedPeriod.ecobot_rg_rake + (selectedPeriod.ecobot_aof_rake || 0));
        const ecobotShrink = Math.max(0, (selectedPeriod.orig_total || selectedPeriod.total) - selectedPeriod.total);
        const ecobotPoolAmount = selectedPeriod.ecobot_pool != null ? selectedPeriod.ecobot_pool : 0;
        const ecobotPoolPct = selectedPeriod.ecobot_pool_pct != null ? selectedPeriod.ecobot_pool_pct : 0;

        const prevEcobotPoolAmount = prevPeriod && prevPeriod.ecobot_pool != null ? prevPeriod.ecobot_pool : null;
        const prevEcobotPoolPct = prevPeriod && prevPeriod.ecobot_pool_pct != null ? prevPeriod.ecobot_pool_pct : null;

        // v2.28.3 自營Bot：跟 EcoBot 同層級顯示。Brad 每兩週會來做技術性調整，
        // 這格就是提醒他「Bot 貢獻了多少水進獎池、回收多少、淨流出多少」——
        // 不醒目顯示的話 Bot 一多就會默默虧掉一筆。
        const sbRow = selfBotRow;  // v2.28.4：元件層算好的，Modal 也用同一份
        const sbWater = sbRow.water || 0;
        const sbPoolIn = sbRow.pool_in || 0;
        // ⚠️ v2.35.0：`recovered` / `net_out` 這一頁**不再顯示**（自營Bot 卡改成
        //    只講「產生多少獎池、佔總額多少」），所以這裡不再取成區域變數。
        //    🔑 但後端 payload 的欄位**不要動** —— 自營Bot 數據頁
        //       (TinySelfBot.js) 的淨流出提示與逐期表格還在讀它們。
        // 自營Bot 佔總額 —— 算法跟 EcoBot 那張卡一致（灌進獎池 ÷ 調整後總獎池）
        const sbPoolPct = selectedPeriod.total > 0 ? (sbPoolIn / selectedPeriod.total * 100) : 0;
        const prevSbRow = (prevPeriod?.contribution_summary?.rows || [])
          .find((r) => r.label === "自營Bot") || {};
        const prevSbPoolPct = (prevPeriod && prevPeriod.total > 0 && prevSbRow.pool_in != null)
          ? (prevSbRow.pool_in / prevPeriod.total * 100) : null;

        return (
          <>
            {/* ⚠️ 原本這裡有一條「獎池模式」橫幅（43px）。它其實是**這期資料的身分**
                   （二元/三元、含不含 AOF、幾家參與），不是控制項也不是數據 ——
                   已經搬進 header 的 badge 與 description，見上面的 TinyPageHeader。
                   公式那句話留在 header 的 meta 裡。 */}

            {/* ⚠️ 這條橫幅原本除了總額，還重複列了輸退／活躍／水量獎池的金額與達標數 ——
                   而底下「俱樂部分配明細」的〈獎池類別〉表**同一組數字全都有**，
                   還多了已分配／未分配（Brad 2026-09-10：「完全重疊，應該只留一個」）。
                → 留下那張表（資訊比較完整），這裡只留獎池總額；
                   輸退／活躍的「vs 上期」已經搬進那張表的〈較上期〉欄。
                🔑 空出來的寬度拿去把下面那三個面板拉上來同一行 —— 兩行 summary
                   併成一行，總高度反而比原本低。 */}
            <div style={{
              display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', gap: 12,
              marginBottom: 14,
            }}>
              {/* 主角：獎池總額。用漸層跟右邊三個平面面板拉開層級 */}
              {/* 主卡：深藍靛實底、**不畫色條**（Brad 2026-09-10 從四個候選中選定）。
                  🔑 它跟右邊三張子卡的層級差別完全靠「底色更深更有顏色 + 數字更大」，
                     不靠邊條也不靠漸層 —— 所以底色不能只是把灰調暗，那樣會變成
                     「同一張卡比較暗」而不是「不同層級」。用的是帶靛的深藍 #1b2547。 */}
              <div style={{
                flex: '1 1 260px', minWidth: 240,
                background: '#1b2547',
                border: '1px solid rgba(129,145,224,0.38)',
                borderRadius: 10, padding: '15px 18px',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
              }}>
                <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 500 }}>獎池總額</div>
                <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                  <span style={{
                    color: '#fff', fontWeight: 700,
                    fontSize: '2.4rem', lineHeight: 1.15,
                  }}>
                    {fmtK(selectedPeriod.total)}
                  </span>
                  <DeltaSpan value={prevPeriod ? selectedPeriod.total - prevPeriod.total : null} />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
                  {selectedPeriod.clubs_count} 個俱樂部 · {isTriPool ? '三元' : '二元'}獎池
                  {' · '}輸退 {fmtK(selectedPeriod.rebate_pool)}
                  {' / '}活躍 {fmtK(selectedPeriod.activity_pool)}
                  {isTriPool && <> / 水量 {fmtK(selectedPeriod.volume_pool)}</>}
                </div>
              </div>

              {/* ── 三個同層級的面板：跟總額同一行 ── */}
              <div style={{ flex: '1 1 230px', minWidth: 220 }}>
                <PoolPanel
                  accent="#0097A7"
                  title="我方回收"
                  primary={{
                    value: fmtK(selectedPeriod.hq_total_return),
                    delta: prevPeriod ? selectedPeriod.hq_total_return - prevPeriod.hq_total_return : null,
                  }}
                  secondary={{
                    label: '佔總額',
                    value: recoveryPct.toFixed(1) + '%',
                    delta: prevRecoveryPct !== null ? recoveryPct - prevRecoveryPct : null,
                    isPct: true,
                  }}
                  detail={(() => {
                    // 三分類：本部 / EcoBot / 外部；外部再逐模式列出非零細項（內部檢視用）
                    const bd = selectedPeriod.hq_return_breakdown || {};
                    const parts = [
                      ['本部', selectedPeriod.hq_hq_return],
                      ['EcoBot', selectedPeriod.hq_eco_return],
                      ['自營Bot', selectedPeriod.hq_self_bot_return],
                      ['Rebate回收', selectedPeriod.hq_rebate_return],
                      ['全回收', bd.recover_only],
                      ['活躍回收', bd.recover_activity],
                      ['輸退回收', bd.recover_loss_refund],
                    ].filter(([, v], i) => i < 2 || v);
                    // pill 呈現：類別淡色、金額白色粗體，一眼可掃
                    //（Brad 反映純文字副標太不明顯）
                    return (
                      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px 6px' }}>
                        {parts.map(([k, v]) => (
                          <span key={k} style={{
                            background: 'rgba(255,255,255,0.07)', borderRadius: 5,
                            padding: '2px 7px', whiteSpace: 'nowrap', lineHeight: 1.4,
                          }}>
                            <span style={{ color: S.textMuted, fontSize: 10 }}>{k} </span>
                            <span style={{ color: S.textPrimary, fontWeight: 700, fontSize: 12 }}>{fmtK(v || 0)}</span>
                          </span>
                        ))}
                      </span>
                    );
                  })()}
                />
              </div>

              <div style={{ flex: '1 1 230px', minWidth: 220 }}>
                <PoolPanel
                  accent="#C62828"
                  title="EcoBot 產生獎池"
                  primary={{
                    value: fmtK(ecobotPoolAmount),
                    delta: prevEcobotPoolAmount !== null ? ecobotPoolAmount - prevEcobotPoolAmount : null,
                  }}
                  secondary={{
                    label: '佔總額',
                    value: ecobotPoolPct.toFixed(1) + '%',
                    delta: prevEcobotPoolPct !== null ? ecobotPoolPct - prevEcobotPoolPct : null,
                    isPct: true,
                  }}
                  detail={ecobotShrink > 0
                    ? `EcoBot水 ${fmtK(ecoWater)} · 已硬扣調整 ${fmtK(ecobotShrink)}`
                    : `EcoBot水 ${fmtK(ecoWater)} × 池率`}
                />
              </div>

              {/* v2.28.3：自營Bot 與 EcoBot 同層級。
                  沒有自營Bot 資料的期別不顯示（不佔版面）。
                  ⚠️ v2.35.0：這張卡改成**跟 EcoBot 完全同一個結構**
                     （產生的獎池 → 佔總額 → 水量 × 池率），
                     Brad 2026-09-10：「你不用管已回收或未回收…讓我看數字就好，
                     不用給我建議」。
                  🚫 所以拿掉了原本的「未回收」副值與
                     「完全沒回收 → 建議技術性調整扣除」那句提示 ——
                     回收與否 Brad 自己看直播判斷、用技術性調整處理，
                     這裡不再替他下結論。
                  ⚠️ 後端 payload 的 `recovered` / `net_out` 欄位不要跟著拿掉 ——
                     自營Bot 數據頁 (TinySelfBot.js) 還在讀它們。 */}
              {sbWater > 0 && (
                <div style={{ flex: '1 1 230px', minWidth: 220 }}>
                  <PoolPanel
                    accent="#2E7D32"
                    title="自營Bot 產生獎池"
                    primary={{
                      value: fmtK(sbPoolIn),
                      delta: prevSbRow.pool_in != null ? sbPoolIn - prevSbRow.pool_in : null,
                    }}
                    secondary={{
                      label: '佔總額',
                      value: sbPoolPct.toFixed(1) + '%',
                      delta: prevSbPoolPct !== null ? sbPoolPct - prevSbPoolPct : null,
                      isPct: true,
                    }}
                    detail={`自營Bot水 ${fmtK(sbWater)} × 池率`}
                  />
                </div>
              )}
            </div>

            {/* v2.28.0：三類「貢獻多少水 → 灌進多少獎池 → 我方回收多少」一覽。
                Brad 的三個要求：回收金額一目了然、看得到各類貢獻多少水、
                自營Bot 拿到的獎金是我方回收也是利潤要在回收項裡。
                ⚠️ 灌入獎池三列相加 ≠ 調整後總獎池 —— 差額是技術性調整縮小的量（全部硬扣在 EcoBot 那列）。 */}
            {/* ⚠️ 「獎池貢獻來源」是**推導過程**（這鍋錢從哪來），不是答案也不是明細，
                   卻夾在 KPI 與俱樂部分配明細中間、佔 249px，把主表推到 y=864。
                   Brad 2026-09-10 確認它是「有問題才查」→ 預設收合。
                🔴 收合列一定要帶關鍵數字（基數水量）—— 見 TinyCollapsibleSection
                   檔頭：這個 repo 已經有「收起來就被忘記」的前例。 */}
            {selectedPeriod.contribution_summary && (
              <TinyCollapsibleSection
                title="獎池貢獻來源"
                icon="fa fa-chart-pie"
                storageKey="biweekly.contribution"
                summary={<>
                  基數水量 {fmt(selectedPeriod.contribution_summary.league_water)}
                  {selectedPeriod.contribution_summary.exclude_aof ? "（排除AOF）" : "（含AOF）"}
                </>}
              >
<Table style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>分類</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>貢獻水量</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>佔比</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>灌入獎池</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedPeriod.contribution_summary.rows.map((r) => (
                    <tr key={r.label}>
                      <td style={tdStyle}>
                        {r.label === 'EcoBot' && <TagBadge tag="bot" />}
                        {r.label === '自營Bot' && <TagBadge tag="selfbot" />}
                        {r.label === '真人' && (
                          <span style={{
                            background: 'rgba(54,162,235,0.25)', color: '#8ecbff', fontSize: 10,
                            fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                          }}>真人</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(r.water)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: S.textMuted }}>{r.water_pct.toFixed(1)}%</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(r.pool_in)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
                <div style={{ padding: '10px 2px 0', color: S.textMuted, fontSize: 11, lineHeight: 1.6 }}>
                  「灌入獎池」＝（該類水量 − 技術性調整扣掉的水）× 池率，三列相加 = 本期總獎池
          {" "}<b style={{ color: S.textSecondary }}>{fmt(selectedPeriod.total)}</b>。
          {selectedPeriod.contribution_summary.pool_in_basis === 'ecobot_absorbs_shrink'
            && (selectedPeriod.orig_total || 0) - (selectedPeriod.total || 0) > 0.005 && (
            <>{" "}本期用舊式（直接填總獎池）調整、沒有分類扣水量明細，縮小額全部歸在 EcoBot 那一列。</>
          )}
                </div>
              </TinyCollapsibleSection>
            )}
          </>
        );
      })()}

      {/* ============================================================ */}
      {/* ===== MODE: Trend (趨勢總覽) ===== */}
      {/* ============================================================ */}

      {/* ===== Zone B: Trend Charts (trend mode only) ===== */}
      {pageMode === "trend" && (
        <Row>
          <Col md="12">
            <div style={{
              background: S.cardBg, border: `1px solid ${S.border}`, borderRadius: '8px',
              marginBottom: 20, overflow: 'hidden',
            }}>
              <div style={{
                background: S.headerBg, padding: '14px 20px',
                borderBottom: `1px solid ${S.border}`,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {[
                  { id: "1", label: "獎池趨勢" },
                  { id: "2", label: "獎池結構" },
                  { id: "3", label: "我方回收" },
                  { id: "4", label: "俱樂部分佈" },
                ].map((tab) => {
                  const isActive = activeChartTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveChartTab(tab.id)}
                      style={{
                        background: isActive ? S.tealGrad : 'transparent',
                        border: isActive ? 'none' : `1px solid ${S.border}`,
                        color: isActive ? '#fff' : 'rgba(255,255,255,0.7)',
                        padding: '6px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.2s',
                        boxShadow: isActive ? '0 3px 8px rgba(0,103,255,0.3)' : 'none',
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {activeChartTab === "4" && (
                <div style={{ padding: '6px 20px 0', color: S.textMuted, fontSize: '11px' }}>
                  依歷期累計獎池金額排名，顯示 Top 5 俱樂部每期獲得的獎池金額，其餘歸入「其他」
                </div>
              )}
              <div style={{ padding: '20px', height: 370 }}>
                {activeChartTab === "1" && trendData.length > 0 && (
                  <Line data={poolTrendChartData} options={baseChartOpts(null)} />
                )}
                {activeChartTab === "2" && trendData.length > 0 && (
                  <Bar data={waterChartData} options={dualAxisOpts(null)} />
                )}
                {activeChartTab === "3" && trendData.length > 0 && (
                  <Bar data={hqRecoveryChartData} options={dualAxisOpts(null)} />
                )}
                {activeChartTab === "4" && clubShareChartData.datasets.length > 0 && (
                  <Bar data={clubShareChartData} options={{
                    ...baseChartOpts(null),
                    scales: {
                      ...baseChartOpts(null).scales,
                      x: { ...baseChartOpts(null).scales?.x, stacked: true },
                      y: { ...baseChartOpts(null).scales?.y, stacked: true },
                    },
                  }} />
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ===== Zone C: Period Detail (detail mode only) ===== */}
      {pageMode === "detail" && selectedPool && (
        <Row>
          <Col md="12">
            <div style={{
              background: S.cardBg, border: `1px solid ${S.border}`, borderRadius: '8px',
              overflow: 'hidden',
            }}>
              {/* Detail Header */}
              <div style={{
                background: S.headerBg, padding: '16px 24px',
                borderBottom: `1px solid ${S.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <h5 style={{ color: S.textPrimary, fontWeight: 600, marginBottom: 0, fontSize: '16px' }}>
                    俱樂部分配明細
                  </h5>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(102,126,234,0.12)', borderRadius: '8px', padding: '6px 16px',
                    border: '1px solid rgba(102,126,234,0.25)',
                  }}>
                    <span style={{ color: S.accent, fontSize: '15px', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                      {selectedPool.week1_id} + {selectedPool.week2_id}
                    </span>
                    {selectedPool.exclude_aof && (
                      <span style={{
                        background: 'rgba(251,99,64,0.2)', color: '#fb6340',
                        border: '1px solid rgba(251,99,64,0.4)',
                        fontSize: '10px', fontWeight: 600,
                        padding: '2px 8px', borderRadius: '6px',
                      }}>排除AOF</span>
                    )}
                    {hasAdjusted && (
                      <span style={{
                        background: 'rgba(155,81,224,0.2)', color: '#9b51e0',
                        border: '1px solid rgba(155,81,224,0.45)',
                        fontSize: '10px', fontWeight: 700,
                        padding: '2px 8px', borderRadius: '6px',
                      }}>
                        {currentVariant === 'adjusted' ? '⚙ 調整版' : '本期有調整版'}
                      </span>
                    )}
                    {selectedMttStatus === 'affected' && (
                      <span
                        title="本期活躍分數用的是舊計算：靠 MTT 參與補分的玩家未被計入，活躍獎池分配偏差。歷史未回溯，數字維持當時結算原樣。詳見上方「關於活躍分數計算調整」說明。"
                        style={{
                          background: 'rgba(245,54,92,0.2)', color: '#f5365c',
                          border: '1px solid rgba(245,54,92,0.45)',
                          fontSize: '10px', fontWeight: 700,
                          padding: '2px 8px', borderRadius: '6px', cursor: 'help',
                        }}>⚠ MTT 活躍分數 BUG</span>
                    )}
                    {selectedMttStatus === 'fixed' && selectedBiweekId === firstFixedBiweekId && (
                      <span
                        title="自本期起，MTT 參與補分已正確計入活躍分數。先前期數的偏差未回溯。"
                        style={{
                          background: 'rgba(45,206,137,0.2)', color: '#2dce89',
                          border: '1px solid rgba(45,206,137,0.4)',
                          fontSize: '10px', fontWeight: 700,
                          padding: '2px 8px', borderRadius: '6px', cursor: 'help',
                        }}>✓ 活躍分數已修復</span>
                    )}
                  </div>
                </div>
                {/* ⚠️ 這一排原本是三種完全不同的視覺處理：切換用青綠漸層、
                       技術性調整用紫色實心漸層、調整版整包用紫色描邊
                      （Brad 2026-09-10：「很像大雜燴」）。
                    🔑 它們**確實是三種不同的東西**，所以該有層級 —— 但要用同一套 token：
                       ① 原版／調整版 = 切換 → segmented，active 用 navActive（跟 tab 同色）
                       ② 技術性調整   = 主要動作（開 modal、會改帳）→ 唯一一顆實心
                       ③ 調整版整包   = 次要動作（下載）→ ghost
                    三者同高 32px、同圓角 7、邊框恆 1px（不會因狀態換而跳動）。 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* ① 切換 */}
                  {hasAdjusted && (
                    <div style={{
                      display: 'flex', height: 32, boxSizing: 'border-box',
                      border: `1px solid ${S.border}`, borderRadius: 7,
                      background: 'rgba(0,0,0,0.2)', padding: 2, gap: 2,
                    }}>
                      {[{ k: 'original', t: '原版' }, { k: 'adjusted', t: '調整版' }].map((v) => {
                        const active = (clubsVariant === v.k) || (clubsVariant === 'effective' && currentVariant === v.k);
                        return (
                          /* ⚠️ active 是**白字**（Brad 2026-09-10）。
                             🔑 所以底色不能用實心亮青 —— 白字壓在 #11cdef 上對比只有
                                ~1.9:1，會糊掉。改成亮青淡染 + 亮青邊框：一樣讀得出
                                「這格被選中、而且是導覽色系」，白字也才看得清楚。 */
                          <button key={v.k} onClick={() => setClubsVariant(v.k)} style={{
                            background: active ? 'rgba(17,205,239,0.20)' : 'transparent',
                            color: active ? '#fff' : S.textMuted,
                            border: `1px solid ${active ? COLORS.navActive : 'transparent'}`,
                            borderRadius: 5, padding: '0 13px',
                            fontSize: 12, fontWeight: 600, lineHeight: 1,
                            cursor: active ? 'default' : 'pointer',
                            transition: 'background 0.16s ease, color 0.16s ease, border-color 0.16s ease',
                          }}>{v.t}</button>
                        );
                      })}
                    </div>
                  )}

                  {/* ② 技術性調整
                      ⚠️ 這顆與下面「下載調整版整包」**同一種處理方式、都不用漸層**
                         （淡底 + 同色邊框 + 同色字，站上既有的按鈕語言），
                         但**色相要分開** —— Brad 2026-09-10：「不要都用紫色…做一點區別」。
                      🔑 分色的依據是**這顆會不會改到帳**：
                         技術性調整會改動獎池分配 → 用 warning 橘（#fb6340），
                         它在站上的語意就是「這一步會影響數字，看清楚再按」；
                         下載只是把現有結果打包、不改任何東西 → 中性灰。
                      🚫 這裡不用 accent 紫（撞頁面上其他紫色元素）也不用亮青
                         （亮青是導覽色，保留給 tab / active 狀態）。
                      v2.28.4：技術性調整的唯一入口，自動帶入自營Bot 建議扣除
                     （只在「本期還沒有調整版」時帶，已存過的調整必須原樣還原，
                       否則打開看到的數字會跟已送出的不一致）。
                      按鈕標籤不顯示金額 —— 帶了什麼進去在 Modal 裡看得到。 */}
                  <button
                    onClick={() => openAdjustModal({ prefillSelfBot: selfBotRow.water || 0 })}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,99,64,0.26)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(251,99,64,0.15)'; }}
                    style={{
                      height: 32, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center',
                      background: 'rgba(251,99,64,0.15)', border: '1px solid rgba(251,99,64,0.5)',
                      color: COLORS.warning, padding: '0 14px', borderRadius: 7,
                      fontSize: 12, fontWeight: 600, lineHeight: 1, cursor: 'pointer',
                      transition: 'background 0.16s ease',
                    }}>
                    <i className="fa fa-gear" style={{ marginRight: 6 }}></i>技術性調整
                  </button>

                  {/* ③ 下載調整版整包 —— 同一種處理方式，但中性色（不改帳，只是打包） */}
                  {hasAdjusted && (
                    <button
                      onClick={() => downloadFile(
                        `${API_BASE}/biweekly/download-all/${selectedBiweekId}?variant=adjusted`,
                        `${selectedBiweekId}__adj_biweekly_reports.zip`)}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      style={{
                        height: 32, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center',
                        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.26)',
                        color: S.textSecondary, padding: '0 14px', borderRadius: 7,
                        fontSize: 12, fontWeight: 600, lineHeight: 1, cursor: 'pointer',
                        transition: 'background 0.16s ease',
                      }}
                    >
                      <i className="fa fa-cloud-arrow-down" style={{ marginRight: 6 }}></i>下載調整版整包
                    </button>                  )}
                </div>
              </div>

              <div style={{ padding: '20px 24px' }}>
                {/* Pool Summary Mini Table */}
                <div style={{ maxWidth: 880, marginBottom: 24 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr>
                        {/* ⚠️ 「較上期」這一欄是從上面的紫色 KPI 橫幅搬下來的 ——
                               輸退/活躍的金額、達標數本來就在這張表裡，上面再寫一次
                               是同一組數字出現兩個地方（Brad 2026-09-10）。
                               留下這張表（它多了已分配/未分配），把 vs 上期併進來。 */}
                        {["獎池類別", "總金額", "較上期", "已分配", "未分配", "達標俱樂部", "總俱樂部"].map((h, i) => (
                          <th key={h} style={{
                            ...thStyle,
                            textAlign: i === 0 ? 'left' : i >= 5 ? 'center' : 'right',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {viewPeriod?.volume_pool > 0 && (
                        <tr>
                          <td style={tdStyle}>水量獎池</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(viewPeriod.volume_pool)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <DeltaSpan plain
                              value={prevPeriod ? (viewPeriod?.volume_pool || 0) - (prevPeriod.volume_pool || 0) : null}
                              pct={prevPeriod && (prevPeriod.volume_pool || 0) !== 0
                                ? ((viewPeriod?.volume_pool || 0) - (prevPeriod.volume_pool || 0)) / (prevPeriod.volume_pool || 0) * 100
                                : null} />
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(viewPeriod.volume_pool)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>-</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>{viewPeriod?.clubs_count}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={tdStyle}>輸退獎池</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(viewPeriod?.rebate_pool)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <DeltaSpan plain
                              value={prevPeriod ? (viewPeriod?.rebate_pool || 0) - (prevPeriod.rebate_pool || 0) : null}
                              pct={prevPeriod && (prevPeriod.rebate_pool || 0) !== 0
                                ? ((viewPeriod?.rebate_pool || 0) - (prevPeriod.rebate_pool || 0)) / (prevPeriod.rebate_pool || 0) * 100
                                : null} />
                          </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(viewPeriod?.rebate_pool)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{viewPeriod?.rebate_qualified}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{viewPeriod?.clubs_count}</td>
                      </tr>
                      <tr>
                        <td style={tdStyle}>活躍獎池</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(viewPeriod?.activity_pool)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <DeltaSpan plain
                              value={prevPeriod ? (viewPeriod?.activity_pool || 0) - (prevPeriod.activity_pool || 0) : null}
                              pct={prevPeriod && (prevPeriod.activity_pool || 0) !== 0
                                ? ((viewPeriod?.activity_pool || 0) - (prevPeriod.activity_pool || 0)) / (prevPeriod.activity_pool || 0) * 100
                                : null} />
                          </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(viewPeriod?.activity_pool)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{viewPeriod?.activity_qualified}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{viewPeriod?.clubs_count}</td>
                      </tr>
                      <tr style={{ background: 'rgba(102,126,234,0.1)', borderTop: `2px solid rgba(102,126,234,0.3)` }}>
                        <td style={{ ...tdStyle, fontWeight: 700, color: S.textPrimary }}>總計</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary }}>{fmt(viewPeriod?.total)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <DeltaSpan plain
                            value={prevPeriod ? (viewPeriod?.total || 0) - (prevPeriod.total || 0) : null}
                            pct={prevPeriod && (prevPeriod.total || 0) !== 0
                              ? ((viewPeriod?.total || 0) - (prevPeriod.total || 0)) / (prevPeriod.total || 0) * 100
                              : null} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary }}>{fmt(viewPeriod?.total)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary }}>0.00</td>
                        <td style={tdStyle}></td><td style={tdStyle}></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Filter Buttons */}
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                  <FilterBtn fKey="all" label="全部" />
                  <FilterBtn fKey="bot" label="EcoBot" />
                  <FilterBtn fKey="hq" label="本部" />
                  <FilterBtn fKey="external" label="外部" />
                  <FilterBtn fKey="qualified" label="達標" />
                  <FilterBtn fKey="rebate" label="Rebate 模式" />
                  <FilterBtn fKey="pure_share" label="回收模式" />
                  <span style={{ color: S.textMuted, fontSize: '12px', marginLeft: 8 }}>
                    顯示 {sortedClubs.length}/{clubsData.length} 個俱樂部
                  </span>
                  <span style={{
                    color: 'rgba(31,142,241,0.6)', fontSize: '11px', marginLeft: 12,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                    <i className="fa fa-hand-pointer" style={{ fontSize: '13px' }}></i>
                    點擊俱樂部名稱可查看歷期明細
                  </span>
                </div>

                {/* Main Club Table */}
                {clubsLoading && !clubsData.length ? (
                  <TinyLoading variant="block" text="載入週期資料" />
                ) : (
                <TinyLoadingOverlay active={clubsLoading} text="載入週期資料">
                {(() => {
                  // 用 viewPeriod（跟著原版／調整版切換，與這張表的資料同一份），不用 trend 的 selectedPeriod
                  const showVolume = viewPeriod?.pool_type === "三元";
                  return (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <SortHeader field="club_id" label="俱樂部ID" />
                          <SortHeader field="club_name" label="俱樂部名稱" />
                          <SortHeader field="rg_total_rake" label="RG總水(雙周)" />
                          <SortHeader field="pre_rake_pl_exclude_jp" label="RG 水前輸贏(扣JP)" />
                          <SortHeader field="rebate_pool" label="輸退獎池" suffix={(() => {
                            const p = periodRebatePct(sortedClubs,
                              (c) => (c.allocation || {}).rebate_pool,
                              (c) => (c.data || {}).pre_rake_pl_exclude_jp);
                            return p === null ? null : (
                              <span style={{ color: S.accent, fontWeight: 600, marginLeft: 4 }}>({p.toFixed(1)}%)</span>
                            );
                          })()} />
                          <SortHeader field="active_score" label="活躍分數" />
                          <SortHeader field="activity_pool" label="活躍獎池" />
                          {showVolume && <SortHeader field="main_pool" label="水量獎池" />}
                          <SortHeader field="pool_total" label="獎池總計" />
                          <th style={{ ...thStyle, textAlign: 'right' }}>佔比%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedClubs.map((club) => {
                          const data = club.data || {};
                          const alloc = club.allocation || {};
                          const elig = club.eligibility || {};
                          const poolTotal = club.pool_total || 0;
                          const sharePct = viewPeriod?.total > 0
                            ? (poolTotal / viewPeriod.total * 100) : 0;
                          const isBot = club.tag === "bot";
                          const isHq = club.tag === "hq";

                          const rowBg = isBot
                            ? 'rgba(251,99,64,0.15)'
                            : isHq
                              ? 'rgba(17,205,239,0.13)'
                              : 'transparent';

                          return (
                            <tr key={club.club_id} style={{ background: rowBg }}>
                              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '12px' }}>{club.club_id}</td>
                              <td style={tdStyle}>
                                <span
                                  style={{ cursor: "pointer", color: '#1f8ef1', fontWeight: 500, borderBottom: '1px dashed rgba(31,142,241,0.4)' }}
                                  onClick={() => openClubHistory(club.club_id)}
                                >
                                  {club.club_name}
                                </span>
                                {" "}<TagBadge tag={club.tag} />
                                {club.rebate_snapshot && club.rebate_snapshot.rate != null && (
                                  <span style={{
                                    marginLeft: 6,
                                    background: 'linear-gradient(135deg, #9b51e0, #7b2cbf)',
                                    color: '#fff', fontSize: 10, fontWeight: 700,
                                    padding: '2px 7px', borderRadius: 10,
                                  }}>
                                    <i className="fa fa-exchange" style={{ fontSize: 9, marginRight: 3 }} />
                                    Rebate {club.rebate_snapshot.rate}%
                                  </span>
                                )}
                                {club.pool_mode_snapshot && (
                                  <span style={{
                                    marginLeft: 6,
                                    background: 'linear-gradient(135deg, #e8a33d, #c77f1a)',
                                    color: '#fff', fontSize: 10, fontWeight: 700,
                                    padding: '2px 7px', borderRadius: 10,
                                  }}>
                                    <i className="fa fa-percent" style={{ fontSize: 9, marginRight: 3 }} />
                                    {POOL_MODE_LABELS[club.pool_mode_snapshot] || club.pool_mode_snapshot}
                                  </span>
                                )}
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(data.rg_total_rake)}</td>
                              <td style={{
                                ...tdStyle, textAlign: 'right', fontFamily: 'monospace',
                                color: (data.pre_rake_pl_exclude_jp || 0) < 0 ? "#f5365c" : "#2dce89", fontWeight: 600,
                              }}>
                                {fmt(data.pre_rake_pl_exclude_jp)}
                              </td>
                              <td style={{
                                ...tdStyle, textAlign: 'right', fontFamily: 'monospace',
                                color: !elig.rebate_pool ? 'rgba(255,255,255,0.25)' : S.textSecondary,
                              }}>
                                {fmt(alloc.rebate_pool || 0)}
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>{data.active_score || 0}</td>
                              <td style={{
                                ...tdStyle, textAlign: 'right', fontFamily: 'monospace',
                                color: !elig.activity_pool ? 'rgba(255,255,255,0.25)' : S.textSecondary,
                              }}>
                                {fmt(alloc.activity_pool || 0)}
                              </td>
                              {showVolume && (
                                <td style={{
                                  ...tdStyle, textAlign: 'right', fontFamily: 'monospace',
                                  color: !elig.main_pool ? 'rgba(255,255,255,0.25)' : S.textSecondary,
                                }}>
                                  {fmt(alloc.main_pool || 0)}
                                </td>
                              )}
                              <td style={{
                                ...tdStyle, textAlign: 'right', fontFamily: 'monospace',
                                fontWeight: poolTotal > 0 ? 700 : 400,
                                color: poolTotal > 0 ? S.textPrimary : 'rgba(255,255,255,0.25)',
                              }}>
                                {fmt(poolTotal)}
                                {club.rebate_snapshot && club.rebate_snapshot.rate != null && poolTotal > 0 && (
                                  <div style={{ fontSize: 10, fontWeight: 400, color: '#9aa4bb', marginTop: 2 }}>
                                    → 內部回收
                                  </div>
                                )}
                                {/* 雙週獎池分潤規則：付款方註記 */}
                                {bonusPoolRules
                                  .filter((r) => r.from_club_id === club.club_id)
                                  .map((r) => {
                                    const base = (alloc.rebate_pool || 0) + (alloc.activity_pool || 0);
                                    if (base <= 0) return null;
                                    const amt = base * (r.percentage || 0) / 100;
                                    return (
                                      <div key={r.settlement_id} style={{ fontSize: 10, fontWeight: 600, color: '#fb6340', marginTop: 2 }}>
                                        → 分 ${fmt(amt)} 給 {r.to_club_name || r.to_club_id} ({r.percentage}%)
                                      </div>
                                    );
                                  })}
                                {/* 雙週獎池分潤規則：收款方註記 */}
                                {bonusPoolRules
                                  .filter((r) => r.to_club_id === club.club_id)
                                  .map((r) => {
                                    const fromClub = sortedClubs.find((c) => c.club_id === r.from_club_id);
                                    const fromAlloc = fromClub?.allocation || {};
                                    const fromBase = (fromAlloc.rebate_pool || 0) + (fromAlloc.activity_pool || 0);
                                    if (fromBase <= 0) return null;
                                    const amt = fromBase * (r.percentage || 0) / 100;
                                    return (
                                      <div key={r.settlement_id} style={{ fontSize: 10, fontWeight: 600, color: '#2dce89', marginTop: 2 }}>
                                        ← 收 ${fmt(amt)} 來自 {fromClub?.club_name || r.from_club_id} ({r.percentage}%)
                                      </div>
                                    );
                                  })}
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                                {sharePct.toFixed(2)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{
                          background: 'rgba(102,126,234,0.1)',
                          borderTop: '2px solid rgba(102,126,234,0.3)',
                        }}>
                          <td colSpan="2" style={{ ...tdStyle, fontWeight: 700, color: S.textPrimary }}>總計（驗算）</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>{fmt(tableTotals.rg)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>{fmt(tableTotals.pl)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>{fmt(tableTotals.rebate)}</td>
                          <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 700, color: S.textPrimary }}>{tableTotals.score}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>{fmt(tableTotals.activity)}</td>
                          {showVolume && <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>{fmt(tableTotals.volume)}</td>}
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>{fmt(tableTotals.total)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>100.00%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  );
                })()}
                </TinyLoadingOverlay>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ===== Club History Modal ===== */}
      <Modal
        isOpen={clubHistoryModal} toggle={() => setClubHistoryModal(false)} size="xl"
        modalClassName="biweekly-modal-backdrop"
        contentClassName="biweekly-modal-content"
      >
        <style>{`
          .biweekly-modal-backdrop .modal-backdrop { background: rgba(0,0,0,0.7) !important; }
          .biweekly-modal-content {
            background: #141a24 !important;
            border: 1.5px solid rgba(102,126,234,0.4) !important;
            border-radius: 12px !important;
            box-shadow: 0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(102,126,234,0.15), 0 0 80px rgba(102,126,234,0.08) !important;
          }
        `}</style>
        <ModalHeader
          toggle={() => setClubHistoryModal(false)}
          style={{
            background: 'linear-gradient(135deg, #1a2233 0%, #252d3a 100%)',
            borderBottom: `1.5px solid rgba(102,126,234,0.3)`,
            color: S.textPrimary, borderRadius: '12px 12px 0 0',
            padding: '16px 24px',
          }}
        >
          {clubHistory ? (
            <span style={{ color: S.textPrimary }}>
              {clubHistory.club_name} ({clubHistory.club_id})
              {" "}<TagBadge tag={clubHistory.tag} />
              <span style={{ color: S.textMuted, fontWeight: 400, marginLeft: 8 }}>— 歷期獎池</span>
            </span>
          ) : <span style={{ color: S.textPrimary }}>俱樂部歷史</span>}
        </ModalHeader>
        <ModalBody style={{ background: '#141a24', padding: '24px', borderRadius: '0 0 12px 12px' }}>
          {clubHistoryLoading ? (
            <TinyLoading variant="block" text="載入歷期明細" />
          ) : clubHistory && clubHistory.periods.length > 0 ? (
            <>
              {/* History Chart */}
              <div style={{
                height: 260, marginBottom: 24, padding: '16px',
                background: 'rgba(0,0,0,0.15)', borderRadius: '8px',
                border: `1px solid ${S.borderLight}`,
              }}>
                <Line
                  data={{
                    labels: clubHistory.periods.map((p) => p.label),
                    datasets: [{
                      label: "獎池總計",
                      data: clubHistory.periods.map((p) => p.pool_total),
                      borderColor: CHART_COLORS.total, backgroundColor: "rgba(225,78,202,0.1)",
                      tension: 0.3, fill: true, pointRadius: 5,
                    }],
                  }}
                  options={baseChartOpts(null)}
                />
              </div>
              {/* History Table */}
              {(() => {
                const hasVolume = clubHistory.periods.some((p) => (p.allocation?.main_pool || 0) > 0);
                const rebateBg  = 'rgba(245,54,92,0.08)';
                const rebateThBg = 'rgba(245,54,92,0.18)';
                const activeBg  = 'rgba(17,205,239,0.08)';
                const activeThBg = 'rgba(17,205,239,0.18)';
                const volumeBg  = 'rgba(251,177,64,0.08)';
                const volumeThBg = 'rgba(251,177,64,0.18)';

                const headers = ["期數", "RG 水前輸贏(扣JP)", "輸退資格", "輸退獎池", "活躍分數", "活躍資格", "活躍獎池"];
                if (hasVolume) headers.push("RG總水", "水量資格", "水量獎池");
                else headers.splice(1, 0, "RG總水");
                headers.push("獎池總計", "佔比", "排名");
                const centerCols = new Set(["活躍分數", "排名", "輸退資格", "活躍資格", "水量資格"]);
                const leftCols = new Set(["期數"]);
                const rebateCols = new Set(["RG 水前輸贏(扣JP)", "輸退資格", "輸退獎池"]);
                const activeCols = new Set(["活躍分數", "活躍資格", "活躍獎池"]);
                const volumeCols = new Set(["RG總水", "水量資格", "水量獎池"]);
                const colBg = (h) => rebateCols.has(h) ? rebateThBg : activeCols.has(h) ? activeThBg : volumeCols.has(h) ? volumeThBg : undefined;
                const cellBg = (h) => rebateCols.has(h) ? rebateBg : activeCols.has(h) ? activeBg : volumeCols.has(h) ? volumeBg : undefined;
                return (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          {headers.map((h) => (
                            <th key={h} style={{
                              ...thStyle,
                              background: colBg(h) || thStyle.background,
                              textAlign: leftCols.has(h) ? 'left' : centerCols.has(h) ? 'center' : 'right',
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {clubHistory.periods.map((p) => (
                          <tr key={p.biweek_id}>
                            <td style={tdStyle}>{p.label}</td>
                            {!hasVolume && (
                              <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{fmt(p.rg_total_rake)}</td>
                            )}
                            <td style={{
                              ...tdStyle, textAlign: 'right', fontFamily: 'monospace', background: rebateBg,
                              color: (p.pre_rake_pl_exclude_jp || 0) < 0 ? "#f5365c" : "#2dce89", fontWeight: 600,
                            }}>
                              {fmt(p.pre_rake_pl_exclude_jp)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', background: rebateBg }}>
                              <StatusBadge qualified={p.eligibility?.rebate_pool} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', background: rebateBg }}>
                              {fmt(p.allocation?.rebate_pool || 0)}
                              <RebatePctTag pool={p.allocation?.rebate_pool} preRakePl={p.pre_rake_pl_exclude_jp} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center', background: activeBg }}>{p.active_score || 0}</td>
                            <td style={{ ...tdStyle, textAlign: 'center', background: activeBg }}>
                              <StatusBadge qualified={p.eligibility?.activity_pool} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', background: activeBg }}>{fmt(p.allocation?.activity_pool || 0)}</td>
                            {hasVolume && (
                              <>
                                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', background: volumeBg }}>{fmt(p.rg_total_rake)}</td>
                                <td style={{ ...tdStyle, textAlign: 'center', background: volumeBg }}>
                                  {p.eligibility?.main_pool !== undefined
                                    ? <StatusBadge qualified={p.eligibility?.main_pool} />
                                    : <span style={{ color: S.textMuted, fontSize: '11px' }}>—</span>}
                                </td>
                                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace', background: volumeBg }}>
                                  {(p.allocation?.main_pool || 0) > 0 ? fmt(p.allocation.main_pool) : <span style={{ color: 'rgba(255,255,255,0.25)' }}>—</span>}
                                </td>
                              </>
                            )}
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: S.textPrimary, fontFamily: 'monospace' }}>{fmt(p.pool_total)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{p.share_pct}%</td>
                            <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>{p.rank || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </>
          ) : (
            <div style={{ textAlign: 'center', color: S.textMuted, padding: 40 }}>此俱樂部無獎池歷史數據</div>
          )}
        </ModalBody>
      </Modal>

      {/* ===== 技術性調整 Modal ===== */}
      <Modal isOpen={adjustOpen} toggle={() => setAdjustOpen(false)} size="xl" style={{ maxWidth: 1120 }}>
        <div style={{ background: S.cardBg, borderRadius: 8, border: `1px solid ${S.border}` }}>
          <ModalHeader toggle={() => setAdjustOpen(false)} style={{ background: S.headerBg, borderBottom: `1px solid ${S.border}` }}>
            <span style={{ color: S.textPrimary, fontWeight: 600 }}>
              <i className="fa fa-gear" style={{ marginRight: 8, color: S.accent }} />
              技術性調整 — {selectedPool?.week1_id} + {selectedPool?.week2_id}
            </span>
          </ModalHeader>
          <ModalBody style={{ background: S.cardBg, maxHeight: "78vh", overflowY: "auto" }}>
            <div style={{
              background: "rgba(102,126,234,0.1)", border: "1px solid rgba(102,126,234,0.25)",
              borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: S.textSecondary, lineHeight: 1.6,
            }}>
              調整 eco / 本部 / <b>自營Bot</b> 俱樂部的「水前輸贏(扣JP)」與「扣除水量」後即時試算分配。送出後會重跑並產<b>調整版 Excel</b>，
              且把調整後的<b>本部 / eco / rebate 獎池回收自動更新到營收數據</b>（最新營收）。
              <span style={{ color: "#fbb140" }}>獎池縮小的差額請人工填入「額外費用」</span>當作我方額外淨營收。原版資料永久保留、可隨時切回。
            </div>

            {simResult?.invalid_override_clubs?.length > 0 && (
              <Alert color="warning" style={{ fontSize: 12, padding: "8px 12px" }}>
                ⚠ 以下俱樂部非 eco/本部/自營Bot，水前輸贏調整已被忽略：{simResult.invalid_override_clubs.join("、")}
              </Alert>
            )}

            <Row>
              {/* 左：編輯區 */}
              <Col md="5">
                {/* v2.28.0：改成填「從獎池基數扣掉多少水」，分 EcoBot / 自營Bot 兩格。
                    以前只有一格「總獎池」，Brad 得自己心算，而且看不出兩者各扣了多少。
                    總獎池 = (聯盟基數水量 − 兩格扣除) × 池率，池率從原版反推（≈13%）。 */}
                <div style={{ color: S.accent, fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                  從獎池基數扣除的水量
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ color: S.textSecondary, fontSize: 12, width: 110 }}>
                    <TagBadge tag="bot" /> 扣除水量
                  </span>
                  <Input bsSize="sm" type="number" min="0" value={adjustForm.water?.ecobot ?? ""}
                    placeholder="0"
                    onChange={(e) => updateWater("ecobot", e.target.value)}
                    style={{ ...selectStyle, fontFamily: "monospace", fontSize: 12 }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ color: S.textSecondary, fontSize: 12, width: 110 }}>
                    <TagBadge tag="selfbot" /> 扣除水量
                  </span>
                  <Input bsSize="sm" type="number" min="0" value={adjustForm.water?.self_bot ?? ""}
                    placeholder="0"
                    onChange={(e) => updateWater("self_bot", e.target.value)}
                    style={{ ...selectStyle, fontFamily: "monospace", fontSize: 12 }} />
                </div>
                {/* 自營Bot 的扣除水量有明確答案（= 本期它貢獻進獎池基數的水），
                    不像 EcoBot 要靠 Brad 判斷，所以把建議值放在輸入格旁邊可一鍵帶入。 */}
                {selfBotRow.water > 0 && (
                  <div style={{ fontSize: 11, color: S.textMuted, margin: "-2px 0 8px 118px" }}>
                    建議 {fmt(selfBotRow.water)}
                    {Math.abs(dedNum(adjustForm.water?.self_bot) - selfBotRow.water) <= 0.005 && (
                      <span style={{ color: S.accent, marginLeft: 6 }}>· 已帶入</span>
                    )}
                    {Math.abs(dedNum(adjustForm.water?.self_bot) - selfBotRow.water) > 0.005 && (
                      <button
                        onClick={() => updateWater("self_bot", String(Math.round(selfBotRow.water * 100) / 100))}
                        style={{
                          marginLeft: 8, background: "transparent", border: `1px solid ${S.borderInput}`,
                          color: S.accent, borderRadius: 4, padding: "1px 8px", fontSize: 11, cursor: "pointer",
                        }}
                      >⟳ 帶入</button>
                    )}
                  </div>
                )}
                {/* v2.28.4 雙向連動：這格填「調整後總獎池要變多大」，會反推 EcoBot 該扣多少水。
                    Brad 的決策其實是「這期獎池就發 100 萬」，只能填扣水量的話得自己心算
                    (10,764,021 − x) × 13% = 1,000,000。兩邊哪一格改，另一邊都即時跟著動。 */}
                <div style={{
                  marginTop: 10, marginBottom: 4, padding: "10px 12px", borderRadius: 6,
                  background: S.inputBg, border: `1px solid ${S.border}`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: S.accent, fontSize: 12, fontWeight: 700, width: 110 }}>
                      調整後總獎池
                    </span>
                    <Input bsSize="sm" type="number" min="0"
                      disabled={!poolBasis}
                      value={totalFocused
                        ? totalDraft
                        : (derivedTotalPool != null ? derivedTotalPool.toFixed(2) : "")}
                      placeholder={origTotalPool != null ? fmt(origTotalPool) : "—"}
                      onFocus={() => {
                        setTotalDraft(derivedTotalPool != null ? derivedTotalPool.toFixed(2) : "");
                        setTotalFocused(true);
                      }}
                      onBlur={() => setTotalFocused(false)}
                      onChange={(e) => updateTargetTotal(e.target.value)}
                      style={{ ...selectStyle, fontFamily: "monospace", fontSize: 12 }} />
                  </div>
                  <div style={{ fontSize: 11, color: S.textMuted, marginTop: 8, lineHeight: 1.7 }}>
                    基數水量 {poolBasis ? fmt(poolBasis.base_water) : "—"}
                    {" × 池率 "}
                    {poolBasis ? (poolBasis.pool_rate * 100).toFixed(2) : "—"}%
                    {poolBasis?.exclude_aof ? "（排除AOF）" : ""}
                    <br />
                    原始總獎池 <b style={{ color: S.textPrimary }}>{fmt(origTotalPool)}</b>
                    {derivedTotalPool != null && origTotalPool != null && (
                      <>
                        {" → 縮小 "}
                        <b style={{ color: origTotalPool - derivedTotalPool > 0.005 ? "#2dce89" : S.textMuted }}>
                          {fmt(origTotalPool - derivedTotalPool)}
                        </b>
                      </>
                    )}
                    {simResult?.delta && (
                      <>
                        <br />
                        輸退 {fmt(simResult.delta.rebate_pool_total.adjusted)}
                        {" / 活躍 "}{fmt(simResult.delta.activity_pool_total.adjusted)}
                      </>
                    )}
                  </div>
                  {targetClamped && (
                    <div style={{ fontSize: 11, color: "#fbb140", marginTop: 6 }}>
                      ⚠ 這個目標超過可達上限（EcoBot 扣除已到 0），實際會是 {fmt(derivedTotalPool)}
                    </div>
                  )}
                  {!poolBasis && (
                    <div style={{ fontSize: 11, color: S.textMuted, marginTop: 6 }}>載入獎池基數中…</div>
                  )}
                </div>

                <div style={{ color: S.accent, fontWeight: 700, fontSize: 13, margin: "16px 0 8px" }}>
                  eco / 本部 / 自營Bot 俱樂部 水前輸贏(扣JP)
                </div>
                <div style={{ maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
                  {origClubs.filter((c) => c.tag === "hq" || c.tag === "bot" || c.tag === "selfbot").map((c) => (
                    <div key={c.club_id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                      <span style={{ width: 130, fontSize: 12, color: S.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        <TagBadge tag={c.tag} /> {c.club_name}
                      </span>
                      <Input bsSize="sm" type="number" value={adjustForm.pre[c.club_id] ?? ""}
                        placeholder={fmt(c.data?.pre_rake_pl_exclude_jp)}
                        onChange={(e) => updatePre(c.club_id, e.target.value)}
                        style={{ ...selectStyle, fontFamily: "monospace", fontSize: 12 }} />
                    </div>
                  ))}
                </div>
              </Col>

              {/* 右：差額摘要 */}
              <Col md="7">
                <div style={{ color: S.accent, fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                  差額試算 {simLoading && <TinyBusy text="計算中" style={{ marginLeft: 8 }} />}
                </div>
                {simResult ? (() => {
                  const d = simResult.delta;
                  const Money = ({ o, a, df }) => (
                    <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                      <span style={{ color: S.textMuted }}>{fmt(o)}</span>
                      <span style={{ color: S.textMuted, margin: "0 4px" }}>→</span>
                      <span style={{ color: S.textPrimary, fontWeight: 600 }}>{fmt(a)}</span>
                      <span style={{ marginLeft: 8, fontWeight: 700, color: df > 0 ? "#2dce89" : df < 0 ? "#f5365c" : S.textMuted }}>
                        ({df > 0 ? "+" : ""}{fmt(df)})
                      </span>
                    </span>
                  );
                  // 即時趴數：Brad 改扣水量時眼睛在這欄，這裡看得到才叫「即時看到調整後的趴數」
                  const rbBefore = periodRebatePct(simResult.clubs,
                    (c) => (c.original || {}).rebate_pool,
                    (c) => (c.original || {}).pre_rake_pl_exclude_jp);
                  const rbAfter = periodRebatePct(simResult.clubs,
                    (c) => (c.allocation || {}).rebate_pool,
                    (c) => c.pre_rake_pl_exclude_jp);
                  const rows = [
                    {
                      label: "輸退獎池總額",
                      v: d.rebate_pool_total,
                      note: rbAfter === null ? null : (
                        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600 }}>
                          (
                          {rbBefore !== null && Math.abs(rbAfter - rbBefore) > 0.05 && (
                            <>
                              <span style={{ color: S.textMuted, fontWeight: 400 }}>{rbBefore.toFixed(1)}%</span>
                              <span style={{ color: S.textMuted, margin: "0 3px" }}>→</span>
                            </>
                          )}
                          <span style={{ color: S.accent }}>{rbAfter.toFixed(1)}%</span>
                          )
                        </span>
                      ),
                    },
                    { label: "活躍獎池總額", v: d.activity_pool_total },
                    { label: "總獎池", v: d.total_pool, bold: true },
                    { label: "我方回收 · 本部", v: d.returns.hq },
                    { label: "我方回收 · eco", v: d.returns.eco },
                    { label: "我方回收 · rebate", v: d.returns.rebate },
                    ...(d.returns.self_bot && (d.returns.self_bot.original || d.returns.self_bot.adjusted)
                      ? [{ label: "我方回收 · 自營Bot", v: d.returns.self_bot }] : []),
                    { label: "我方回收 · 合計", v: d.returns.total, bold: true },
                  ];
                  return (
                    <div style={{ background: S.inputBg, border: `1px solid ${S.border}`, borderRadius: 6, padding: "12px 14px" }}>
                      {rows.map((r) => (
                        <div key={r.label} style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "6px 0", borderBottom: `1px solid ${S.borderLight}`,
                        }}>
                          <span style={{ fontSize: 12, color: S.textSecondary, fontWeight: r.bold ? 700 : 400 }}>
                            {r.label}{r.note}
                          </span>
                          <Money o={r.v.original} a={r.v.adjusted} df={r.v.diff} />
                        </div>
                      ))}
                      <div style={{
                        marginTop: 12, padding: "10px 12px", borderRadius: 6,
                        background: "rgba(251,177,64,0.12)", border: "1px solid rgba(251,177,64,0.35)",
                      }}>
                        <div style={{ fontSize: 12, color: "#fbb140", fontWeight: 700 }}>
                          獎池縮小（建議填入「額外費用」的我方額外淨營收）
                        </div>
                        <div style={{ fontFamily: "monospace", fontSize: 18, fontWeight: 700, color: d.pool_shrink > 0 ? "#2dce89" : S.textMuted, marginTop: 4 }}>
                          {fmt(d.pool_shrink)}
                        </div>
                        <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>
                          本部/eco/rebate 回收已自動進營收；此縮小金額需人工登記。
                        </div>
                      </div>
                    </div>
                  );
                })() : (
                  <div style={{ color: S.textMuted, fontSize: 13, padding: 20, textAlign: "center" }}>
                    {simLoading ? "試算中…" : "輸入調整值即可試算"}
                  </div>
                )}
              </Col>
            </Row>

            {/* 試算預覽表 */}
            {simResult && (
              <div style={{ marginTop: 18 }}>
                <div style={{ color: S.accent, fontWeight: 700, fontSize: 13, marginBottom: 4 }}>各俱樂部分配：原版 → 調整後（預覽）</div>
                <div style={{ fontSize: 11, color: S.textMuted, marginBottom: 8 }}>
                  每格<span style={{ color: S.textMuted }}> 上方灰色 = 原版</span>、<span style={{ color: S.textPrimary }}>下方 = 調整後</span>
                </div>
                <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead><tr>
                      {/* 輸退趴數放 header：每家達標俱樂部必定同一個趴數，逐列印 24 次沒意義。
                          左灰=原版、右綠=調整後，跟著試算即時變。 */}
                      {["俱樂部", "水前輸贏(扣JP)", "輸退獎池", "活躍獎池", "獎池總計", "佔比%"].map((h, i) => (
                        <th key={h} style={{ ...thStyle, textAlign: i === 0 ? "left" : "right", position: "sticky", top: 0 }}>
                          {h}
                          {h === "輸退獎池" && (() => {
                            const before = periodRebatePct(simResult.clubs,
                              (c) => (c.original || {}).rebate_pool,
                              (c) => (c.original || {}).pre_rake_pl_exclude_jp);
                            const after = periodRebatePct(simResult.clubs,
                              (c) => (c.allocation || {}).rebate_pool,
                              (c) => c.pre_rake_pl_exclude_jp);
                            if (after === null) return null;
                            const changed = before !== null && Math.abs(after - before) > 0.05;
                            return (
                              <span style={{ marginLeft: 4, fontWeight: 600 }}>
                                (
                                {changed && (
                                  <>
                                    <span style={{ color: S.textMuted, fontWeight: 400 }}>{before.toFixed(1)}%</span>
                                    <span style={{ color: S.textMuted, margin: "0 3px" }}>→</span>
                                  </>
                                )}
                                <span style={{ color: S.accent }}>{after.toFixed(1)}%</span>
                                )
                              </span>
                            );
                          })()}
                        </th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {simResult.clubs.map((c) => {
                        const o = c.original || {};
                        // before(灰) / after(粗) 兩行；after 變動才上色
                        const BA = (before, after, opts = {}) => {
                          const changed = Math.abs((after || 0) - (before || 0)) > 0.005;
                          return (
                            <td style={{ ...tdStyle, textAlign: "right", fontFamily: "monospace", padding: "6px 12px" }}>
                              <div style={{ color: S.textMuted, fontSize: 11 }}>{opts.pct ? `${before}%` : fmt(before)}</div>
                              <div style={{ fontWeight: 600, color: opts.color || (changed ? S.textPrimary : S.textSecondary) }}>
                                {opts.pct ? `${after}%` : fmt(after)}
                              </div>
                            </td>
                          );
                        };
                        return (
                          <tr key={c.club_id} style={{ background: c.is_overridden ? "rgba(102,126,234,0.12)" : "transparent" }}>
                            <td style={{ ...tdStyle }}><TagBadge tag={c.tag} /> {c.club_name}{c.is_overridden && <span style={{ color: S.accent, marginLeft: 6, fontSize: 11 }}>✎</span>}</td>
                            {BA(o.pre_rake_pl_exclude_jp, c.pre_rake_pl_exclude_jp, { color: c.pre_rake_pl_exclude_jp < 0 ? "#f5365c" : "#2dce89" })}
                            {BA(o.rebate_pool, c.allocation?.rebate_pool || 0)}
                            {BA(o.activity_pool, c.allocation?.activity_pool || 0)}
                            {BA(o.pool_total, c.pool_total, { color: S.textPrimary })}
                            {BA(o.share_pct, c.share_pct, { pct: true })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </ModalBody>
          <div style={{ background: S.headerBg, borderTop: `1px solid ${S.border}`, padding: "14px 20px" }}>
            <Input type="text" bsSize="sm" placeholder="調整原因 / 備註（會記錄到調整紀錄）" value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              style={{ ...selectStyle, marginBottom: 12, fontSize: 12 }} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              {existingAdjustment?.adjustment && (
                <Button size="sm" disabled={committing} onClick={handleDelete}
                  style={{ background: S.dangerGrad, border: "none" }}>還原為原版</Button>
              )}
              <Button size="sm" disabled={committing} onClick={() => setAdjustOpen(false)}
                style={{ background: "transparent", border: `1px solid ${S.borderInput}`, color: S.textSecondary }}>取消</Button>
              <Button size="sm" disabled={committing || simLoading} onClick={handleCommit}
                style={{ background: S.successGrad, border: "none" }}>
                {committing ? <><TinyBusy light /> 處理中</> : "送出並重跑"}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default TinyBiweekly;
